import React from "react";

// Helper para normalizar fechas que pueden llegar como string, Date, null o undefined
const normalizeDate = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value === "string") {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime())
      ? null
      : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
  return null;
};

const formatDateInputValue = (value: string | Date | null | undefined): string => {
  const date = normalizeDate(value);
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const dateInputToUtcDate = (value: string): Date => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  FileText,
  Download,
  Eye,
  Upload,
  CreditCard,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  Info,
  Plus,
  Edit,
} from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { ContractAdjustmentsSection } from "./ContractAdjustmentsSection";
import { resolvePlannedBillingMilestoneAmount } from "@/finance/billingMilestoneAmounts";
import { hasCapability, type FinanceRole } from "@shared/finance/capabilities";
import {
  INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL,
  INVOICE_REVIEW_REQUIRED_LABEL,
  billingMilestoneDisplayStatus,
  isInvoiceCollectionComplete,
  isInvoiceCollectionPending,
  isInvoicePaidPendingEconomicReview,
} from "@/finance/billingCollectionPresentation";
import { hasVerifiedBillingInvoiceIdentity, isBillingInvoiceOverdue } from "@shared/billing/billing-due-date";
import { calculateInvoiceVatAmount, type BillingVatRate } from "@shared/billing/invoice-vat";
import { BillingVatRateSelect } from "../billing/BillingVatRateSelect";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type BillingDocument = {
  id: number;
  name: string;
  documentType: string;
  fileUrl: string | null;
  fileKey?: string | null;
  createdAt?: string | Date;
};

type BillingNoticeView = {
  id: number;
  billingMilestoneId: number;
  installationId: number;
  eventType: string;
  status: string;
  recipientEmail: string;
  recipientName: string | null;
  attempt: number;
  emailLogId: number | null;
  supersededReason: string | null;
  errorMessage: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  sentAt: Date | string | null;
  failedAt: Date | string | null;
  supersededAt: Date | string | null;
  cancelledAt: Date | string | null;
};

type Milestone = {
  id: number;
  installationId: number;
  type: "fixed_amount" | "percentage" | "monthly_certification";
  label: string | null;
  description: string | null;
  percentage: number | null;
  fixedAmount: string | null;
  calculatedAmount: string | null;
  dueDate: Date | null;
  paymentDueDate?: Date | null;
  daysOverdue?: number | null;
  isOverdue?: boolean | null;
  paymentMethod: "transfer" | "confirming" | "promissory_note" | "other" | "cash" | null;
  paymentTermBasis:
    | "invoice_date"
    | "end_of_month"
    | "client_acceptance"
    | "other"
    | null;
  paymentTerms: string | null;
  notes: string | null;
  status: string;
  collectionStatus?: string | null;
  certifiedAt: Date | null;
  certifiedBy: number | null;
  invoiceNumber: string | null;
  invoiceDate: Date | null;
  invoiceAmount: string | null;
  invoiceTaxableBase?: string | null;
  invoiceTaxAmount?: string | null;
  invoiceWithholdingAmount?: string | null;
  invoiceFiscalVerified?: boolean;
  invoiceEconomicReviewRequired?: boolean;
  invoiceEconomicReviewMessage?: string | null;
  historicalCollectionConflict?: boolean;
  collectedAmount?: string | null;
  balanceAmount?: string | null;
  collectionEvents?: Array<Record<string, unknown>>;
  invoicePdfUrl: string | null;
  invoicePdfKey: string | null;
  paymentDate: Date | null;
  paymentDelayDays?: number | null;
  paymentAmount: string | null;
  bankCommissionAmount?: string | null;
  bankInterestAmount?: string | null;
  financialCostAmount?: string | null;
  netReceivedAmount?: string | null;
  effectivePaymentMethod?: string | null;
  requiresManualCertificationReview: boolean;
  contractStatus: string | null;
  contractDocumentId: number | null;
  billingConfigStatus: string | null;
  billingConfigValidatedAt: Date | null;
  billingConfigValidatedBy: number | null;
  billingWarningLevel: "none" | "warning" | "critical";
  billingOverrideReason: string | null;
  billingOverrideBy: number | null;
  billingOverrideAt: Date | null;
  accountingNotifiedAt: Date | null;
  accountingNotifiedBy: number | null;
  accountingEmailLogId: number | null;
  requiresEconomicReview?: boolean;
  issueCode?: string | null;
  economicIssueCode?: string | null;
  economicAmount?: string | null;
  economicAmountSource?: string | null;
  suggestedHistoricalBase?: string | null;
  originalContractAmount?: string | null;
  economicBaseAmount?: string | null;
  effectiveContractAmount?: string | null;
  notices?: BillingNoticeView[];
  createdAt: Date;
  updatedAt: Date;
};

export function BillingTab({ installation }: { installation: any }) {
  const { user } = useAuth();
  const isManager = ["admin", "project_manager", "admin_manager"].includes(
    user?.role || ""
  );
  const canWriteMilestoneEconomics = user
    ? hasCapability(user.role as FinanceRole, "finance.write")
    : false;
  const canEditMilestoneMetadata = user
    ? hasCapability(user.role as FinanceRole, "finance.read")
    : false;
  const isFinanceManager = ["admin", "admin_manager"].includes(user?.role || "");
  const utils = trpc.useUtils();

  const isEconomicallyLockedStatus = (status: string) =>
    ["accounting_notified", "invoiced", "sent_to_client", "pending_collection", "collected"].includes(status);
  const isReviewRequiredStatus = (status: string) => status === "ready_to_invoice";

  const parseMoney = (value: unknown): number | null => {
    if (value === undefined || value === null || value === "") return null;
    const parsed = typeof value === "number" ? value : Number(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const parseCollectionPaymentAmount = (value: unknown): number | null => {
    if (value === undefined || value === null || value === "") return null;
    const normalized = String(value).trim();
    if (!/^(?:\d+(?:[.,]\d{1,2})?|[.,]\d{1,2})$/.test(normalized)) return null;
    const parsed = Number(normalized.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatMoney = (value: unknown) => {
    const parsed = parseMoney(value);
    return parsed === null
      ? "—"
      : `${parsed.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' })} €`;
  };

  const getCollectedPaymentLabel = (milestone: Milestone) => {
    const paymentDate = normalizeDate(milestone.paymentDate);
    const netSuffix = milestone.netReceivedAmount
      ? ` · Neto banco: ${formatMoney(milestone.netReceivedAmount)}`
      : "";
    if (!paymentDate) return `Cobrado · sin fecha registrada${netSuffix}`;

    const formattedDate = paymentDate.toLocaleDateString("es-ES");
    if (milestone.paymentDelayDays != null && milestone.paymentDelayDays > 0) {
      return `Cobrado: ${formattedDate} · ${milestone.paymentDelayDays} días de retraso${netSuffix}`;
    }
    if (milestone.paymentDelayDays != null && milestone.paymentDelayDays <= 0) {
      return `Cobrado: ${formattedDate} · en plazo${netSuffix}`;
    }
    return `Cobrado: ${formattedDate}${netSuffix}`;
  };

  const getMilestoneDueDate = (milestone: Milestone) => normalizeDate(milestone.paymentDueDate);

  const getMilestoneDueState = (milestone: Milestone) => {
    if (isInvoiceCollectionComplete(milestone) || isInvoicePaidPendingEconomicReview(milestone)) {
      return { date: getMilestoneDueDate(milestone), isExpected: false, isOverdue: false, daysOverdue: 0 };
    }

    const dueDate = getMilestoneDueDate(milestone);
    if (!dueDate) {
      return { date: normalizeDate(milestone.dueDate), isExpected: true, isOverdue: false, daysOverdue: 0 };
    }
    const isOverdue = Boolean(milestone.isOverdue && isBillingInvoiceOverdue(milestone));
    return {
      date: dueDate,
      isExpected: false,
      isOverdue,
      daysOverdue: isOverdue ? (milestone.daysOverdue ?? 0) : 0,
    };
  };

  const renderMilestoneDueState = (milestone: Milestone) => {
    if (isInvoicePaidPendingEconomicReview(milestone)) {
      return <span className="text-muted-foreground">Factura cobrada</span>;
    }
    const { date, isExpected, isOverdue, daysOverdue } = getMilestoneDueState(milestone);
    if (!date) return <span className="text-muted-foreground">—</span>;

    return (
      <span className={isOverdue ? "font-semibold text-red-600" : undefined}>
        {isExpected ? "Fecha prevista: " : ""}
        {date.toLocaleDateString("es-ES")}
        {isOverdue && daysOverdue > 0 ? ` · ${daysOverdue} días vencido` : ""}
      </span>
    );
  };

  // Helper: Get contract document from installation documents
  const getContractDocument = (): BillingDocument | undefined => {
    const docs = (documentsQuery.data || []) as BillingDocument[];
    return docs.find(d =>
      installation.contractDocumentId
        ? d.id === Number(installation.contractDocumentId)
        : d.documentType === "contract"
    );
  };

  const [showCancelled, setShowCancelled] = React.useState(false);

  // Query milestones
  const milestonesQuery = trpc.billingMilestones.listByInstallation.useQuery({
    installationId: installation.id,
    includeCancelled: showCancelled,
  });
  const ceilingQuery = trpc.contractAdjustments.getEffectiveContractCeiling.useQuery({
    installationId: installation.id,
  });
  const effectiveContractAmount =
    (ceilingQuery.data as any)?.effectiveContractAmount ??
    (ceilingQuery.data as any)?.effectiveContractCeiling ??
    null;
  const originalContractAmount =
    (ceilingQuery.data as any)?.originalContractAmount ??
    installation.installationPrice ??
    null;
  const approvedIncreaseAmount = (ceilingQuery.data as any)?.approvedIncreaseAmount ?? null;
  const approvedReductionAmount = (ceilingQuery.data as any)?.approvedReductionAmount ?? null;
  const committedAmount = (ceilingQuery.data as any)?.committedAmount ?? null;
  const remainingToAllocate = (ceilingQuery.data as any)?.remainingToAllocate ?? null;
  const effectiveContractAmountNumber = parseMoney(effectiveContractAmount);
  const remainingToAllocateNumber = parseMoney(remainingToAllocate);
  const effectiveContractAmountUnavailable =
    ceilingQuery.isLoading || ceilingQuery.isError || effectiveContractAmountNumber === null || effectiveContractAmountNumber <= 0;

  // Query documents
  const documentsQuery = trpc.documents.listByInstallation.useQuery({
    installationId: installation.id,
  });

  // Mutations for create/update
  const createMutation = trpc.billingMilestones.create.useMutation();
  const updateMutation = trpc.billingMilestones.update.useMutation();
  const repairLegacyBaseMutation =
    trpc.billingMilestones.repairLegacyBase.useMutation();

  // Mutations for status actions
  const markReadyMutation = trpc.billingMilestones.markReady.useMutation();
  const notifyAccountingMutation =
    trpc.billingMilestones.notifyAccounting.useMutation();
  const markInvoicedMutation =
    trpc.billingMilestones.markInvoiced.useMutation();
  const markSentToClientMutation = trpc.billingMilestones.markSentToClient.useMutation();
  const markPendingCollectionMutation =
    trpc.billingMilestones.markPendingCollection.useMutation();
  const recordCollectionMutation =
    trpc.billingMilestones.recordCollection.useMutation();
  const attachInvoicePdfMutation =
    trpc.billingMilestones.attachInvoicePdf.useMutation();
  const cancelMutation = trpc.billingMilestones.cancel.useMutation();
  const revertStatusMutation = trpc.billingMilestones.revertStatus.useMutation();
  const uploadDocumentMutation = trpc.documents.upload.useMutation();
  const updateBillingConfigMutation =
    trpc.installations.updateBillingConfig.useMutation();

  // Form state for create/edit
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editingMilestone, setEditingMilestone] =
    React.useState<Milestone | null>(null);
  const [formType, setFormType] = React.useState<"create" | "edit">("create");
  const [formErrors, setFormErrors] = React.useState<Record<string, string>>(
    {}
  );
  const [percentageBaseMode, setPercentageBaseMode] = React.useState<"original" | "effective" | "manual">("original");

  // Form data state
  const [formData, setFormData] = React.useState<{
    type: "fixed_amount" | "percentage" | "monthly_certification";
    label: string;
    description: string;
    percentage: string;
    amount: string;
    fixedAmount: string;
    calculationBase: string;
    calculatedAmount: string;
    dueDate: string;
    paymentMethod: string;
    paymentTermBasis: string;
    paymentTerms: string;
    notes: string;
  }>({
    type: "fixed_amount",
    label: "",
    description: "",
    percentage: "",
    amount: "",
    fixedAmount: "",
    calculationBase: "",
    calculatedAmount: "",
    dueDate: "",
    paymentMethod: "transfer",
    paymentTermBasis: "invoice_date",
    paymentTerms: "",
    notes: "",
  });
  const isMetadataOnlyMilestoneEdit =
    formType === "edit" &&
    !!editingMilestone &&
    canEditMilestoneMetadata &&
    !canWriteMilestoneEconomics;

  const applyPercentageBaseMode = (mode: "original" | "effective" | "manual") => {
    setPercentageBaseMode(mode);
    if (mode === "manual") return;
    const base = mode === "effective" ? effectiveContractAmount : originalContractAmount;
    if (base) {
      setFormData(prev => ({ ...prev, calculationBase: String(base) }));
    }
  };

  // Status action state
  const [actionMilestone, setActionMilestone] =
    React.useState<Milestone | null>(null);
  const [cancelReason, setCancelReason] = React.useState("");
  const [revertReason, setRevertReason] = React.useState("");
  const [revertTargetStatus, setRevertTargetStatus] = React.useState("");
  const [invoiceNumber, setInvoiceNumber] = React.useState("");
  const [invoiceDate, setInvoiceDate] = React.useState("");
  const [invoiceFile, setInvoiceFile] = React.useState<File | null>(null);
  const [collectedDate, setCollectedDate] = React.useState("");
  const [grossPaymentAmount, setGrossPaymentAmount] = React.useState("");
  const [collectionMethod, setCollectionMethod] = React.useState("transfer");
  const [collectionReference, setCollectionReference] = React.useState("");
  const [collectionIdempotencyKey, setCollectionIdempotencyKey] = React.useState("");
  const [bankCommissionAmount, setBankCommissionAmount] = React.useState("0.00");
  const [bankInterestAmount, setBankInterestAmount] = React.useState("0.00");
  const [invoiceTaxableBase, setInvoiceTaxableBase] = React.useState("");
  const [invoiceTaxAmount, setInvoiceTaxAmount] = React.useState("");
  const [invoiceVatRate, setInvoiceVatRate] = React.useState<BillingVatRate>("manual");
  const [invoiceWithholdingAmount, setInvoiceWithholdingAmount] = React.useState("");
  const handleInvoiceBaseChange = (value: string) => {
    setInvoiceTaxableBase(value);
    if (invoiceVatRate !== "manual") {
      const calculated = calculateInvoiceVatAmount(value, invoiceVatRate);
      if (calculated !== null) setInvoiceTaxAmount(calculated);
    }
  };
  const handleInvoiceVatRateChange = (value: string) => {
    const rate = value as BillingVatRate;
    setInvoiceVatRate(rate);
    const calculated = calculateInvoiceVatAmount(invoiceTaxableBase, rate);
    if (calculated !== null) setInvoiceTaxAmount(calculated);
  };
  const [loadingAction, setLoadingAction] = React.useState<string | null>(null);
  const [statusDialog, setStatusDialog] = React.useState<
    | "confirm_status"
    | "mark_invoiced"
    | "cancel"
    | "revert_status"
    | "repair_legacy_base"
    | "confirming_settlement"
    | null
  >(null);
  const [legacyBaseConfirmed, setLegacyBaseConfirmed] = React.useState(false);
  const [pendingStatusAction, setPendingStatusAction] = React.useState<{
    action: "mark_ready" | "notify_accounting" | "mark_pending_collection";
    milestone: Milestone;
    newStatus: string;
  } | null>(null);
  const contractFileInputRef = React.useRef<HTMLInputElement>(null);
  const invoiceFileInputRefs = React.useRef<
    Record<number, HTMLInputElement | null>
  >({});

  const refreshBillingViews = async () => {
    await Promise.all([
      milestonesQuery.refetch(),
      ceilingQuery.refetch(),
      utils.billingMilestones.listByInstallation.invalidate({
        installationId: installation.id,
        includeCancelled: showCancelled,
      }),
      utils.contractAdjustments.getEffectiveContractCeiling.invalidate({
        installationId: installation.id,
      }),
      utils.installations.list.invalidate(),
      utils.installations.getById.invalidate({ id: installation.id }),
    ]);
  };

  // Helper functions
  const getMilestoneStatusBadge = (milestone: Milestone, invoicePaidPendingReview = false) => {
    const statusColors: Record<string, string> = {
      pending: "bg-gray-500",
      ready_to_invoice: "bg-blue-500",
      invoiced: "bg-green-500",
      sent_to_client: "bg-cyan-500",
      pending_collection: "bg-yellow-500",
      collected: "bg-emerald-500",
      cancelled: "bg-red-500",
      accounting_notified: "bg-purple-500",
    };
    const statusLabels: Record<string, string> = {
      pending: "Pendiente",
      ready_to_invoice: "Listo para factura",
      invoiced: "Facturado",
      sent_to_client: "Enviado al cliente",
      pending_collection: "Cobro pendiente",
      collected: "Cobrado",
      cancelled: "Cancelado",
      accounting_notified: "Notificado a contabilidad",
      invoice_review: INVOICE_REVIEW_REQUIRED_LABEL,
    };
    const status = billingMilestoneDisplayStatus(milestone);
    return (
      <Badge
        className={invoicePaidPendingReview ? "bg-amber-100 text-amber-900 whitespace-normal text-left" : statusColors[status] || "bg-gray-500"}
        data-testid={invoicePaidPendingReview ? "invoice-paid-economic-review-status" : undefined}
      >
        {invoicePaidPendingReview ? INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL : statusLabels[status] || status}
      </Badge>
    );
  };

  const getMilestoneStatusLabel = (status: string, milestone?: Milestone) => {
    if (milestone && isInvoicePaidPendingEconomicReview(milestone)) return INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL;
    const labels: Record<string, string> = {
      pending: "Pendiente",
      ready_to_invoice: "Listo para factura",
      invoiced: "Facturado",
      sent_to_client: "Enviado al cliente",
      pending_collection: "Cobro pendiente",
      collected: "Cobrado",
      cancelled: "Cancelado",
      accounting_notified: "Notificado a contabilidad",
      invoice_review: INVOICE_REVIEW_REQUIRED_LABEL,
    };
    const displayStatus = milestone ? billingMilestoneDisplayStatus(milestone) : status;
    return labels[displayStatus] || displayStatus;
  };

  const getEffectiveNoticeStatus = (notice: BillingNoticeView) => {
    if (notice.status === "pending" && (notice.sentAt || notice.emailLogId != null)) return "sent";
    return notice.status;
  };

  const getNoticeStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      sent: "Enviado",
      failed: "Fallido",
      superseded: "Invalidado por retroceso",
      pending: "Pendiente",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  };

  const getNoticeStatusBadge = (status: string) => {
    const classNames: Record<string, string> = {
      sent: "border-emerald-200 bg-emerald-50 text-emerald-700",
      failed: "border-red-200 bg-red-50 text-red-700",
      superseded: "border-slate-200 bg-slate-50 text-slate-700",
      pending: "border-amber-200 bg-amber-50 text-amber-700",
      cancelled: "border-gray-200 bg-gray-50 text-gray-700",
    };

    return (
      <Badge variant="outline" className={classNames[status] || "border-gray-200 bg-gray-50 text-gray-700"}>
        {getNoticeStatusLabel(status)}
      </Badge>
    );
  };

  const formatNoticeDateTime = (value: Date | string | null | undefined) => {
    if (!value) return "—";
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? "—" : date.toLocaleString("es-ES");
  };

  const getNoticeMainDate = (notice: BillingNoticeView) =>
    notice.sentAt || notice.failedAt || notice.supersededAt || notice.cancelledAt || notice.createdAt;

  const getLatestAccountingNotice = (milestone: Milestone) => {
    const notices = [...(milestone.notices || [])].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return bTime - aTime || b.id - a.id;
    });
    return notices[0] ?? null;
  };

  const renderAccountingNoticeSummary = (milestone: Milestone, compact = false) => {
    const notice = getLatestAccountingNotice(milestone);
    const historyHref = `/installations/${installation.id}?tab=history`;

    if (!notice) {
      return (
        <div className={compact ? "space-y-1 text-xs" : "space-y-2 text-xs"}>
          <div className="font-medium text-gray-700">Último aviso a Contabilidad</div>
          <div className="text-muted-foreground">Sin avisos registrados.</div>
          <a className="text-ods-navigation hover:underline" href={historyHref}>Ver historial completo</a>
        </div>
      );
    }

    const rawStatus = getEffectiveNoticeStatus(notice);
    // If the notice record is stuck as "pending" (no sentAt, no emailLogId) but the
    // milestone has accountingNotifiedAt set, the milestone-level audit is authoritative
    // — treat as "sent" to avoid misleading the user with stale notice data.
    const status =
      rawStatus === 'pending' && !notice.sentAt && notice.emailLogId == null && milestone.accountingNotifiedAt
        ? 'sent'
        : rawStatus;
    return (
      <div className={compact ? "space-y-1 text-xs" : "space-y-2 text-xs"}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-700">Último aviso a Contabilidad</span>
          {getNoticeStatusBadge(status)}
        </div>
        <div className="grid gap-1 text-gray-600">
          <div className="break-words [overflow-wrap:anywhere]">
            <span className="text-muted-foreground">Destinatario:</span>{" "}
            {notice.recipientName || notice.recipientEmail}
          </div>
          <div>
            <span className="text-muted-foreground">Intento:</span>{" "}
            <span className="font-medium">{notice.attempt}</span>
            <span className="mx-1 text-muted-foreground">·</span>
            <span className="text-muted-foreground">Fecha:</span>{" "}
            {formatNoticeDateTime(getNoticeMainDate(notice))}
          </div>
          {notice.emailLogId != null ? (
            <div className="text-muted-foreground">emailLogId {notice.emailLogId}</div>
          ) : null}
          {status === "superseded" && notice.supersededAt ? (
            <div className="text-slate-600">
              Invalidado: {formatNoticeDateTime(notice.supersededAt)}
            </div>
          ) : null}
          {status === "superseded" && notice.supersededReason ? (
            <div className="text-slate-600 break-words [overflow-wrap:anywhere]">
              Motivo: {notice.supersededReason}
            </div>
          ) : null}
          {status === "failed" && notice.errorMessage ? (
            <div className="text-red-600 break-words [overflow-wrap:anywhere]">
              Error: {notice.errorMessage}
            </div>
          ) : null}
        </div>
        <a className="text-ods-navigation hover:underline" href={historyHref}>Ver historial completo</a>
      </div>
    );
  };

  const getMilestoneTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      fixed_amount: "Importe fijo",
      percentage: "Porcentaje",
      monthly_certification: "Certificación mensual",
    };
    return labels[type] || type;
  };

  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return "No especificado";
    const labels: Record<string, string> = {
      transfer: "Transferencia",
      confirming: "Confirming",
      promissory_note: "Pagaré",
      pagare: "Pagaré",
      cash: "Contado",
      other: "Otro",
    };
    return labels[method] || method;
  };

  const getPaymentTermLabel = (term: string | null) => {
    if (!term) return "No especificado";
    const labels: Record<string, string> = {
      invoice_date: "Fecha factura",
      certification_date: "Fecha certificación",
      acceptance_date: "Fecha aceptación",
      due_date: "Fecha prevista",
      end_of_month: "Fin de mes",
      client_acceptance: "Aceptación cliente",
      other: "Otro",
    };
    return labels[term] || term;
  };

  // Helper functions for status actions
  const canShowMarkReady = (milestone: Milestone): boolean =>
    milestone.status === "pending";

  const canShowNotifyAccounting = (milestone: Milestone): boolean =>
    milestone.status === "ready_to_invoice" && !!milestone.dueDate;

  const canShowNotifyAccountingWithWarning = (milestone: Milestone): boolean =>
    milestone.status === "ready_to_invoice" && !milestone.dueDate;

  const canShowMarkInvoiced = (milestone: Milestone): boolean =>
    ["ready_to_invoice", "accounting_notified"].includes(milestone.status);

  const canShowMarkSentToClient = (milestone: Milestone): boolean =>
    milestone.status === "invoiced" && !canShowMarkCollected(milestone);

  const canShowMarkPendingCollection = (milestone: Milestone): boolean =>
    milestone.status === "sent_to_client" && !canShowMarkCollected(milestone);

  const canShowMarkCollected = (milestone: Milestone): boolean =>
    isFinanceManager
    && ["invoiced", "sent_to_client", "pending_collection"].includes(milestone.status)
    && Boolean(milestone.invoiceNumber?.trim())
    && normalizeDate(milestone.invoiceDate) !== null
    && milestone.invoiceFiscalVerified === true
    && (parseMoney(milestone.balanceAmount) ?? 0) > 0;

  const canShowInvoiceFiscalRepair = (milestone: Milestone): boolean =>
    isFinanceManager
    && Boolean(milestone.invoiceNumber)
    && milestone.invoiceFiscalVerified !== true
    && ["invoiced", "sent_to_client", "pending_collection", "collected"].includes(milestone.status);

  const canShowCancel = (milestone: Milestone): boolean =>
    ["pending", "ready_to_invoice", "accounting_notified"].includes(milestone.status);

  const canEditBillingMilestone = (milestone: Milestone): boolean =>
    (canWriteMilestoneEconomics || canEditMilestoneMetadata) &&
    ["pending", "ready_to_invoice"].includes(milestone.status);

  const hasInvoiceData = (milestone: Milestone): boolean =>
    Boolean(
      milestone.invoiceNumber ||
      milestone.invoiceDate ||
      milestone.invoicePdfUrl ||
      milestone.invoicePdfKey
    );

  const hasAccountingNotice = (milestone: Milestone): boolean =>
    Boolean(
      milestone.accountingNotifiedAt ||
      milestone.accountingNotifiedBy ||
      milestone.accountingEmailLogId
    );

  const getAllowedRevertTargets = (milestone: Milestone): string[] => {
    if (milestone.status === "pending_collection") return ["sent_to_client"];
    if (milestone.status === "invoiced") {
      return hasAccountingNotice(milestone)
        ? ["accounting_notified", "ready_to_invoice"]
        : ["ready_to_invoice"];
    }
    if (milestone.status === "accounting_notified") return ["ready_to_invoice"];
    if (milestone.status === "ready_to_invoice") return ["pending"];
    return [];
  };

  const canShowRevertStatus = (milestone: Milestone): boolean =>
    getAllowedRevertTargets(milestone).length > 0;

  const isActionLoading = (action: string, milestone: Milestone) =>
    loadingAction === `${action}:${milestone.id}`;

  const closeStatusDialog = () => {
    setStatusDialog(null);
    setActionMilestone(null);
    setCancelReason("");
    setRevertReason("");
    setRevertTargetStatus("");
    setInvoiceNumber("");
    setInvoiceDate("");
    setInvoiceFile(null);
    setCollectedDate("");
    setGrossPaymentAmount("");
    setBankCommissionAmount("0.00");
    setBankInterestAmount("0.00");
    setPendingStatusAction(null);
    setLegacyBaseConfirmed(false);
  };

  const handleStatusActionError = (error: any, fallback: string) => {
    toast.error(error?.message || fallback);
  };

  const getMilestoneBaseLabel = (milestone: Milestone) => {
    if (milestone.type !== "percentage" || !milestone.percentage) return "—";
    if (isEconomicallyLockedStatus(milestone.status)) {
      return `Base utilizada al comunicar/facturar: ${milestone.fixedAmount ? formatMoney(milestone.fixedAmount) : "Base histórica no disponible"} • ${milestone.percentage}%`;
    }
    if (isReviewRequiredStatus(milestone.status)) {
      return `Base lista para facturar: ${milestone.economicBaseAmount ? formatMoney(milestone.economicBaseAmount) : (milestone.fixedAmount ? formatMoney(milestone.fixedAmount) : "Base histórica no disponible")} • ${milestone.percentage}%`;
    }
    return milestone.fixedAmount
      ? `${milestone.percentage}% sobre base ${formatMoney(milestone.fixedAmount)}`
      : "Base histórica no disponible";
  };

  const getApprovedMilestoneAmount = (milestone: Milestone) =>
    resolvePlannedBillingMilestoneAmount(milestone);

  const getCollectionObligationAmount = (milestone: Milestone) =>
    // A milestone obligation is not a receivable until a valid invoice exists.
    hasVerifiedBillingInvoiceIdentity(milestone) ? milestone.invoiceAmount : null;

  const getMilestoneAmountLabel = (milestone: Milestone) => {
    const plannedAmount = resolvePlannedBillingMilestoneAmount(milestone);
    return plannedAmount === null ? "No verificable" : formatMoney(plannedAmount);
  };

  const getEconomicIssueCode = (milestone: Milestone) =>
    milestone.issueCode || milestone.economicIssueCode || null;

  const getPersistedMilestoneAmount = (milestone: Milestone) => {
    // For percentage milestones fixedAmount is the historical base, not the milestone amount.
    // An invoice total is never a substitute for either value.
    if (milestone.type === "percentage" || milestone.type === "monthly_certification") {
      return milestone.calculatedAmount;
    }
    return milestone.fixedAmount;
  };

  const getSuggestedHistoricalBase = (milestone: Milestone): number | null => {
    const provided = parseMoney(milestone.suggestedHistoricalBase);
    if (provided !== null && provided > 0) return provided;
    const amount = parseMoney(getPersistedMilestoneAmount(milestone));
    const percentage = parseMoney(milestone.percentage);
    if (amount === null || percentage === null || percentage <= 0) return null;
    return amount / (percentage / 100);
  };

  const canRepairLegacyBase = (milestone: Milestone) => {
    if (milestone.type !== "percentage") return false;
    if (getEconomicIssueCode(milestone) !== "HISTORICAL_BASE_MISSING") return false;
    return canWriteMilestoneEconomics && ["pending", "ready_to_invoice"].includes(milestone.status);
  };

  const getLegacyBaseValue = () => {
    const suggestedBase = actionMilestone ? getSuggestedHistoricalBase(actionMilestone) : null;
    return suggestedBase !== null ? suggestedBase.toFixed(2) : "";
  };

  const openRepairLegacyBaseDialog = (milestone: Milestone) => {
    setActionMilestone(milestone);
    setLegacyBaseConfirmed(false);
    setStatusDialog("repair_legacy_base");
  };

  const openStatusConfirmation = (
    action: "mark_ready" | "notify_accounting" | "mark_pending_collection",
    milestone: Milestone,
    newStatus: string
  ) => {
    setActionMilestone(milestone);
    setPendingStatusAction({ action, milestone, newStatus });
    setStatusDialog("confirm_status");
  };

  const openConfirmingSettlementDialog = (milestone: Milestone) => {
    setActionMilestone(milestone);
    setCollectedDate(new Date().toISOString().slice(0, 10));
    setGrossPaymentAmount("");
    setCollectionMethod(milestone.effectivePaymentMethod ?? milestone.paymentMethod ?? "transfer");
    setCollectionReference("");
    setCollectionIdempotencyKey(crypto.randomUUID());
    setBankCommissionAmount("0.00");
    setBankInterestAmount("0.00");
    setStatusDialog("confirming_settlement");
  };

  const handleConfirmingSettlementSubmit = async () => {
    if (!actionMilestone) return;
    const isConfirming = collectionMethod === "confirming";

    const gross = parseCollectionPaymentAmount(grossPaymentAmount);
    if (!collectedDate) {
      toast.error("Indica la fecha de cobro.");
      return;
    }
    if (gross === null || gross <= 0) {
      toast.error("El importe bruto satisfecho debe ser válido, mayor que cero y tener como máximo dos decimales.");
      return;
    }
    if (actionMilestone.invoiceFiscalVerified !== true) {
      toast.error("La factura no tiene un desglose fiscal verificable. Completa o revisa sus datos antes de registrar cobros.");
      return;
    }
    const remaining = parseMoney(actionMilestone.balanceAmount);
    if (remaining === null || remaining <= 0) {
      toast.error("No existe saldo exigible verificable en una factura pendiente.");
      return;
    }
    if (Math.round(gross * 100) > Math.round(remaining * 100)) {
      toast.error("El cobro supera el saldo de la factura; el excedente no se aplicará automáticamente.");
      return;
    }

    let commission = 0;
    let interest = 0;
    if (isConfirming) {
      commission = bankCommissionAmount === "" ? 0 : parseMoney(bankCommissionAmount) ?? 0;
      interest = bankInterestAmount === "" ? 0 : parseMoney(bankInterestAmount) ?? 0;
      if (commission < 0 || interest < 0) {
        toast.error("La comisión y los intereses deben ser importes válidos no negativos.");
        return;
      }
      if (commission + interest > gross) {
        toast.error("La suma de comisión e intereses no puede superar el importe bruto satisfecho.");
        return;
      }
    }

    setLoadingAction(`mark_collected:${actionMilestone.id}`);
    try {
      const result = await recordCollectionMutation.mutateAsync({
        billingMilestoneId: actionMilestone.id,
        occurredAt: new Date(`${collectedDate}T12:00:00`),
        amount: gross.toFixed(2),
        paymentMethod: collectionMethod as "transfer" | "confirming" | "promissory_note" | "other" | "cash",
        reference: collectionReference.trim() || undefined,
        idempotencyKey: collectionIdempotencyKey,
        ...(isConfirming
          ? {
              bankCommissionAmount: bankCommissionAmount || "0.00",
              bankInterestAmount: bankInterestAmount || "0.00",
            }
          : {}),
      });
      closeStatusDialog();
      toast.success(Number(result.balanceAmount) > 0
        ? "Cobro parcial registrado; la factura conserva saldo pendiente."
        : result.invoiceEconomicReviewRequired
          ? "Cobro registrado; la diferencia económica requiere revisión antes de cerrar el hito."
          : "Factura liquidada y hito cerrado.");
      await refreshBillingViews();
    } catch (error: any) {
      handleStatusActionError(error, "Error al registrar el cobro");
    } finally {
      setLoadingAction(null);
    }
  };

  const getConfirmingNetAmount = () => {
    const gross = parseCollectionPaymentAmount(grossPaymentAmount);
    const commission = bankCommissionAmount === "" ? 0 : parseMoney(bankCommissionAmount);
    const interest = bankInterestAmount === "" ? 0 : parseMoney(bankInterestAmount);
    if (gross === null || commission === null || interest === null) return null;
    return gross - commission - interest;
  };

  const runStatusMutation = async (
    action:
      | "mark_ready"
      | "notify_accounting"
      | "mark_sent_to_client"
      | "mark_pending_collection",
    milestone: Milestone
  ) => {
    setLoadingAction(`${action}:${milestone.id}`);
    try {
      if (action === "mark_ready") {
        await markReadyMutation.mutateAsync({ id: milestone.id });
        toast.success("Hito marcado como listo para facturar");
      } else if (action === "notify_accounting") {
        await notifyAccountingMutation.mutateAsync({ id: milestone.id });
        toast.success("Aviso enviado a Contabilidad");
      } else if (action === "mark_sent_to_client") {
        await markSentToClientMutation.mutateAsync({ id: milestone.id });
        toast.success("Envío al cliente registrado; no se ha enviado ningún correo desde la aplicación");
      } else if (action === "mark_pending_collection") {
        await markPendingCollectionMutation.mutateAsync({ id: milestone.id });
        toast.success("Hito marcado como pendiente de cobro");
      }
      await refreshBillingViews();
    } catch (error: any) {
      handleStatusActionError(error, "Error al actualizar el estado del hito");
    } finally {
      setLoadingAction(null);
    }
  };

  const openMarkInvoicedDialog = (milestone: Milestone) => {
    setActionMilestone(milestone);
    setInvoiceNumber(milestone.invoiceNumber || "");
    setInvoiceDate(
      milestone.invoiceDate
        ? new Date(milestone.invoiceDate).toISOString().split("T")[0]
        : ""
    );
    setInvoiceFile(null);
    const plannedBase = resolvePlannedBillingMilestoneAmount(milestone);
    setInvoiceTaxableBase(milestone.invoiceTaxableBase ?? String(plannedBase ?? ""));
    setInvoiceTaxAmount(milestone.invoiceTaxAmount ?? "");
    setInvoiceVatRate("manual");
    setInvoiceWithholdingAmount(milestone.invoiceWithholdingAmount ?? "");
    setStatusDialog("mark_invoiced");
  };

  const openCancelDialog = (milestone: Milestone) => {
    setActionMilestone(milestone);
    setCancelReason("");
    setStatusDialog("cancel");
  };

  const openRevertStatusDialog = (milestone: Milestone) => {
    const targets = getAllowedRevertTargets(milestone);
    setActionMilestone(milestone);
    setRevertTargetStatus(targets[0] || "");
    setRevertReason("");
    setStatusDialog("revert_status");
  };

  const handleContractFileSelected = async (file: File | null) => {
    if (!file) return;
    setLoadingAction("upload_contract");
    try {
      const uploadResult = await uploadDocumentMutation.mutateAsync({
        installationId: installation.id,
        name: file.name,
        documentType: "contract",
        fileData: await readFileAsBase64(file),
        mimeType: file.type || "application/octet-stream",
        description: "Contrato de facturación",
      });
      if (!uploadResult.documentId) {
        throw new Error("No se pudo asociar el contrato a la instalación");
      }
      await updateBillingConfigMutation.mutateAsync({
        id: installation.id,
        contractDocumentId: uploadResult.documentId,
        contractStatus: "contract_signed",
      });
      toast.success("Contrato adjuntado");
      await documentsQuery.refetch();
    } catch (error: any) {
      handleStatusActionError(error, "Error al adjuntar el contrato");
    } finally {
      setLoadingAction(null);
      if (contractFileInputRef.current) contractFileInputRef.current.value = "";
    }
  };

  const handleInvoiceFileSelected = async (
    milestone: Milestone,
    file: File | null
  ) => {
    if (!file) return;
    setLoadingAction(`attach_invoice:${milestone.id}`);
    try {
      await attachInvoicePdfMutation.mutateAsync({
        id: milestone.id,
        invoiceFileName: file.name,
        invoiceFileData: await readFileAsBase64(file),
        invoiceFileMimeType: file.type || "application/octet-stream",
      });
      toast.success("Factura adjuntada");
      await refreshBillingViews();
    } catch (error: any) {
      handleStatusActionError(error, "Error al adjuntar la factura");
    } finally {
      setLoadingAction(null);
      const input = invoiceFileInputRefs.current[milestone.id];
      if (input) input.value = "";
    }
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const value = reader.result;
        if (typeof value !== "string") {
          reject(new Error("No se pudo leer el archivo de factura"));
          return;
        }
        resolve(value.split(",")[1] || "");
      };
      reader.onerror = () =>
        reject(
          reader.error || new Error("No se pudo leer el archivo de factura")
        );
      reader.readAsDataURL(file);
    });

  const handleMarkInvoiced = async () => {
    if (!actionMilestone) return;
    if (!invoiceNumber.trim() || !invoiceDate) {
      toast.error("Número y fecha de factura son obligatorios");
      return;
    }
    const base = parseMoney(invoiceTaxableBase);
    const tax = parseMoney(invoiceTaxAmount);
    const withholding = parseMoney(invoiceWithholdingAmount);
    if (base === null || base <= 0 || tax === null || tax < 0 || withholding === null || withholding < 0 || base + tax - withholding <= 0) {
      toast.error("Indica base imponible, IVA y retención válidos; usa 0,00 cuando no corresponda.");
      return;
    }

    setLoadingAction(`mark_invoiced:${actionMilestone.id}`);
    try {
      const invoiceFilePayload = invoiceFile
        ? {
            invoiceFileName: invoiceFile.name,
            invoiceFileData: await readFileAsBase64(invoiceFile),
            invoiceFileMimeType: invoiceFile.type || "application/octet-stream",
          }
        : {};

      await markInvoicedMutation.mutateAsync({
        id: actionMilestone.id,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate: new Date(invoiceDate),
        invoiceTaxableBase: base.toFixed(2),
        invoiceTaxAmount: tax.toFixed(2),
        invoiceWithholdingAmount: withholding.toFixed(2),
        invoiceAmount: (base + tax - withholding).toFixed(2),
        ...invoiceFilePayload,
      });
      toast.success("Hito marcado como facturado");
      await refreshBillingViews();
      closeStatusDialog();
    } catch (error: any) {
      handleStatusActionError(error, "Error al marcar como facturado");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleCancel = async () => {
    if (!actionMilestone) return;
    const reason = cancelReason.trim();
    if (reason.length < 5 || reason.length > 500) {
      toast.error("El motivo debe tener entre 5 y 500 caracteres.");
      return;
    }

    setLoadingAction(`cancel:${actionMilestone.id}`);
    try {
      await cancelMutation.mutateAsync({
        id: actionMilestone.id,
        cancellationReason: reason,
      });
      toast.success("Hito cancelado");
      await refreshBillingViews();
      closeStatusDialog();
    } catch (error: any) {
      handleStatusActionError(error, "Error al cancelar el hito");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRevertStatus = async () => {
    if (!actionMilestone) return;
    const reason = revertReason.trim();
    if (!reason) {
      toast.error("Indica un motivo para retroceder el estado.");
      return;
    }
    if (reason.length < 5 || reason.length > 500) {
      toast.error("El motivo debe tener entre 5 y 500 caracteres.");
      return;
    }
    if (!revertTargetStatus) {
      toast.error("No se puede retroceder este hito desde su estado actual.");
      return;
    }

    setLoadingAction(`revert_status:${actionMilestone.id}`);
    try {
      await revertStatusMutation.mutateAsync({
        id: actionMilestone.id,
        targetStatus: revertTargetStatus as any,
        reason,
      });
      toast.success("Estado del hito retrocedido");
      await refreshBillingViews();
      closeStatusDialog();
    } catch (error: any) {
      handleStatusActionError(error, "Error al retroceder el estado del hito");
    } finally {
      setLoadingAction(null);
    }
  };

  const renderEconomicReviewWarning = (milestone: Milestone) => {
    if (milestone.status === "cancelled") return null;
    if (milestone.historicalCollectionConflict) {
      return (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="historical-collected-ledger-conflict">
          <div className="font-medium">Estado «Cobrado» conservado; saldo discrepante</div>
          <div>{milestone.invoiceEconomicReviewMessage || "El saldo del ledger no se convierte automáticamente en deuda; requiere conciliación."}</div>
        </div>
      );
    }
    if (!milestone.requiresEconomicReview) return null;
    const isHistoricalBaseMissing = getEconomicIssueCode(milestone) === "HISTORICAL_BASE_MISSING";
    const suggestedBase = getSuggestedHistoricalBase(milestone);
    const persistedAmount = getPersistedMilestoneAmount(milestone);

    if (isHistoricalBaseMissing) {
      return (
        <div className="mt-2 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="font-medium">Base histórica pendiente de confirmar.</div>
          <div className="grid gap-1 text-amber-800">
            <div>Porcentaje: {milestone.percentage ?? "-"}%</div>
            <div>Importe persistido: {formatMoney(persistedAmount)}</div>
            <div>Base sugerida: {suggestedBase !== null ? formatMoney(suggestedBase) : "-"}</div>
            <div>Contrato original: {formatMoney(milestone.originalContractAmount || originalContractAmount)}</div>
            <div>Contrato vigente: {formatMoney(milestone.effectiveContractAmount || effectiveContractAmount)}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
        <div className="font-medium">Revisión económica necesaria</div>
        <div>Este hito no tiene un importe económico verificable.</div>
      </div>
    );
  };

  const handleRepairLegacyBase = async () => {
    if (!actionMilestone) return;
    const confirmedBase = getLegacyBaseValue();
    if (!confirmedBase || parseMoney(confirmedBase) === null || (parseMoney(confirmedBase) ?? 0) <= 0) {
      toast.error("La base histórica confirmada debe ser mayor que 0");
      return;
    }
    if (!legacyBaseConfirmed) {
      toast.error("Confirma expresamente la base histórica antes de guardar");
      return;
    }

    setLoadingAction(`repair_legacy_base:${actionMilestone.id}`);
    try {
      await repairLegacyBaseMutation.mutateAsync({
        id: actionMilestone.id,
        confirmedBase,
        confirmation: true,
      });
      toast.success("Base histórica confirmada");
      await refreshBillingViews();
      closeStatusDialog();
    } catch (error: any) {
      handleStatusActionError(error, "Error al confirmar la base histórica");
    } finally {
      setLoadingAction(null);
    }
  };

  const renderMonthlyCertificationWarning = (milestone: Milestone | null) => {
    if (!milestone || milestone.type !== "monthly_certification") return null;
    return (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            <strong>Aviso:</strong> Este hito es de tipo certificación mensual y
            requiere revisión manual antes de proceder.
          </p>
        </div>
      </div>
    );
  };

  const renderStatusActions = (milestone: Milestone) => {
    if (!isManager) return null;

    const disabled = loadingAction !== null;
    const economicActionDisabled = disabled || effectiveContractAmountUnavailable;
    const actions = [];

    if (canShowMarkReady(milestone)) {
      actions.push(
        <Button
          key="mark_ready"
          variant="outline"
          size="sm"
          onClick={() =>
            openStatusConfirmation(
              "mark_ready",
              milestone,
              "Listo para factura"
            )
          }
          disabled={economicActionDisabled}
          className="h-7 px-2 text-xs"
        >
          {ceilingQuery.isLoading ? "..." : isActionLoading("mark_ready", milestone) ? "..." : "Marcar listo"}
        </Button>
      );
    }

    if (milestone.requiresEconomicReview && canRepairLegacyBase(milestone)) {
      actions.push(
        <Button
          key="repair_legacy_base"
          variant="outline"
          size="sm"
          onClick={() => openRepairLegacyBaseDialog(milestone)}
          disabled={disabled}
          className="h-7 px-2 text-xs"
        >
          {isActionLoading("repair_legacy_base", milestone) ? "..." : "Confirmar base histórica"}
        </Button>
      );
    }

    if (canShowNotifyAccounting(milestone)) {
      actions.push(
        <Button
          key="notify_accounting"
          variant="outline"
          size="sm"
          onClick={() =>
            openStatusConfirmation(
              "notify_accounting",
              milestone,
              "Notificado a contabilidad"
            )
          }
          disabled={economicActionDisabled || milestone.requiresEconomicReview}
          className="h-7 px-2 text-xs"
        >
          {ceilingQuery.isLoading
            ? "..."
            : isActionLoading("notify_accounting", milestone)
            ? "..."
            : "Avisar a Contabilidad"}
        </Button>
      );
    } else if (canShowNotifyAccountingWithWarning(milestone)) {
      actions.push(
        <Button
          key="notify_accounting"
          variant="outline"
          size="sm"
          disabled={true}
          className="h-7 px-2 text-xs opacity-50 cursor-not-allowed"
        >
          Avisar a Contabilidad (sin fecha)
        </Button>
      );
    }

    if (milestone.status === "accounting_notified") {
      actions.push(
        <Badge
          key="accounting_notified"
          variant="outline"
          className="h-7 px-2 text-xs"
        >
          Avisado a Contabilidad
        </Badge>
      );
    }

    if (canShowMarkInvoiced(milestone)) {
      actions.push(
        <Button
          key="mark_invoiced"
          variant="outline"
          size="sm"
          onClick={() => openMarkInvoicedDialog(milestone)}
          disabled={disabled}
          className="h-7 px-2 text-xs"
        >
          {isActionLoading("mark_invoiced", milestone)
            ? "..."
            : "Marcar facturado"}
        </Button>
      );
    }

    if (canShowMarkSentToClient(milestone)) {
      actions.push(<Button key="mark_sent_to_client" variant="outline" size="sm" onClick={() => runStatusMutation("mark_sent_to_client", milestone)} disabled={disabled} className="h-7 px-2 text-xs">Registrar envío al cliente</Button>);
    }

    if (canShowMarkPendingCollection(milestone)) {
      actions.push(
        <Button
          key="mark_pending_collection"
          variant="outline"
          size="sm"
          onClick={() =>
            openStatusConfirmation(
              "mark_pending_collection",
              milestone,
              "Cobro pendiente"
            )
          }
          disabled={disabled}
          className="h-7 px-2 text-xs"
        >
          {isActionLoading("mark_pending_collection", milestone)
            ? "..."
            : "Marcar pendiente de cobro"}
        </Button>
      );
    }

    if (canShowInvoiceFiscalRepair(milestone)) {
      actions.push(
        <Button key="verify_invoice_fiscal" variant="outline" size="sm" onClick={() => openMarkInvoicedDialog(milestone)} disabled={disabled} className="h-7 px-2 text-xs">
          Completar desglose fiscal
        </Button>,
      );
    }

    if (canShowMarkCollected(milestone)) {
      actions.push(
        <Button
          key="mark_collected"
          variant="outline"
          size="sm"
          onClick={() => {
            openConfirmingSettlementDialog(milestone);
          }}
          disabled={disabled}
          className="h-7 px-2 text-xs"
        >
          {isActionLoading("mark_collected", milestone)
            ? "..."
            : "Registrar cobro"}
        </Button>
      );
    }

    if (canShowCancel(milestone)) {
      actions.push(
        <Button
          key="cancel"
          variant="destructive"
          size="sm"
          onClick={() => openCancelDialog(milestone)}
          disabled={disabled}
          className="h-7 px-2 text-xs"
        >
          {isActionLoading("cancel", milestone) ? "..." : "Cancelar"}
        </Button>
      );
    }

    if (canShowRevertStatus(milestone)) {
      actions.push(
        <Button
          key="revert_status"
          variant="outline"
          size="sm"
          onClick={() => openRevertStatusDialog(milestone)}
          disabled={disabled}
          className="h-7 px-2 text-xs"
        >
          {isActionLoading("revert_status", milestone)
            ? "..."
            : "Retroceder estado"}
        </Button>
      );
    }

    return actions.length > 0 ? (
      <div className="flex min-w-44 flex-col gap-1 sm:flex-row sm:flex-wrap">
        {actions}
      </div>
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  };

  // Effect to handle status actions when loadingAction changes

  // Helper for banner conditions - use real installation fields
  const workOrderType = installation.workOrderType || "installation";
  const contractStatus = installation.contractStatus;
  const billingConfigStatus = installation.billingConfigStatus;
  const maintenancePaymentStatus = installation.maintenancePaymentStatus;
  // Explicit persisted flag only. Do not infer contracted maintenance from
  // workOrderType or maintenancePaymentStatus.
  const hasContractedMaintenance = installation.includesMaintenance === true;
  const billingWarningLevel = installation.billingWarningLevel || "none";

  // Contract status: pending if installation type and contract not signed/not required
  const hasPendingContract =
    workOrderType === "installation" &&
    contractStatus !== "contract_signed" &&
    contractStatus !== "contract_not_required";

  // Provisional billing: monthly_certification type OR warning/critical level
  const isProvisionalBilling = milestonesQuery.data
    ? milestonesQuery.data.some(
        (m: Milestone) => m.type === "monthly_certification"
      )
    : false;
  const hasWarningOrCritical =
    billingWarningLevel === "warning" || billingWarningLevel === "critical";
  const showProvisionalBanner =
    isProvisionalBilling ||
    billingConfigStatus === "billing_provisional" ||
    hasWarningOrCritical;

  // Render form modal
  // Economic milestone creation is restricted to finance writers.
  const renderCreateButton = () => {
    if (!canWriteMilestoneEconomics) return null;
    return (
      <div className="sticky bottom-3 z-20 flex justify-end rounded-lg bg-background/95 p-2 shadow-lg ring-1 ring-border/60 sm:static sm:bg-transparent sm:p-0 sm:shadow-none sm:ring-0">
        <Button onClick={openCreateModal} disabled={effectiveContractAmountUnavailable} className="w-full gap-2 sm:w-auto">
          <Plus className="w-4 h-4" />
          Crear hito
        </Button>
      </div>
    );
  };

  const renderBillingDocuments = () => {
    if (!isManager) return null;

    const invoiceMilestones = milestones.filter((milestone: Milestone) =>
      ["invoiced", "sent_to_client", "pending_collection", "collected"].includes(milestone.status)
    );

    // INVOICE_FILE_URL_PUBLIC_ACCESS_CAVEAT:
    // current storage URLs are consumed directly by the browser. Secure download routing
    // is intentionally left out of FINANCE_BILLING_DOCUMENTS_P1.
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-primary">
            <FileText className="w-5 h-5" />
            Facturas asociadas a hitos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            {invoiceMilestones.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No hay facturas adjuntas a hitos de facturación.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <table className="min-w-[760px] text-left text-sm">
                  <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Nº factura</th>
                      <th className="px-4 py-3">Hito / concepto</th>
                      <th className="px-4 py-3">Fecha factura</th>
                      <th className="px-4 py-3">Archivo</th>
                      <th className="px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invoiceMilestones.map((milestone: Milestone) => (
                      <tr key={milestone.id}>
                        <td className="px-4 py-3 font-medium">
                          {milestone.invoiceNumber || "—"}
                        </td>
                        <td className="px-4 py-3">
                          {milestone.label || milestone.description || "—"}
                        </td>
                        <td className="px-4 py-3">
                          {milestone.invoiceDate
                            ? new Date(
                                milestone.invoiceDate
                              ).toLocaleDateString("es-ES")
                            : "—"}
                        </td>
                        <td className="px-4 py-3">
                          {milestone.invoicePdfKey ||
                          milestone.invoicePdfUrl ? (
                            <span className="inline-flex items-center gap-1.5">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              {milestone.invoicePdfKey?.split("/").pop() ||
                                "Factura adjunta"}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            <input
                              ref={el => {
                                invoiceFileInputRefs.current[milestone.id] = el;
                              }}
                              type="file"
                              className="hidden"
                              accept="application/pdf,image/*"
                              onChange={e =>
                                handleInvoiceFileSelected(
                                  milestone,
                                  e.target.files?.[0] || null
                                )
                              }
                            />
                            {milestone.invoicePdfUrl ? (
                              <>
                                <Button size="sm" variant="outline" asChild>
                                  <a
                                    href={milestone.invoicePdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    Ver factura
                                  </a>
                                </Button>
                                <Button size="sm" variant="outline" asChild>
                                  <a href={milestone.invoicePdfUrl} download>
                                    <Download className="mr-2 h-4 w-4" />
                                    Descargar factura
                                  </a>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    invoiceFileInputRefs.current[
                                      milestone.id
                                    ]?.click()
                                  }
                                  disabled={
                                    loadingAction ===
                                    `attach_invoice:${milestone.id}`
                                  }
                                >
                                  <Upload className="mr-2 h-4 w-4" />
                                  {loadingAction ===
                                  `attach_invoice:${milestone.id}`
                                    ? "Subiendo..."
                                    : "Reemplazar factura"}
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  invoiceFileInputRefs.current[
                                    milestone.id
                                  ]?.click()
                                }
                                disabled={
                                  loadingAction ===
                                  `attach_invoice:${milestone.id}`
                                }
                              >
                                <Upload className="mr-2 h-4 w-4" />
                                {loadingAction ===
                                `attach_invoice:${milestone.id}`
                                  ? "Subiendo..."
                                  : "Adjuntar factura"}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render form modal
  const renderFormModal = () => {
    if (!isModalOpen) return null;

    return (
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="max-h-[90dvh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {formType === "create"
                ? "Crear nuevo hito de facturación"
                : "Editar hito de facturación"}
            </DialogTitle>
            <DialogDescription>
              {formType === "create"
                ? "Rellena los datos para crear un nuevo hito de facturación."
                : isMetadataOnlyMilestoneEdit
                  ? "Solo puedes editar la descripción, la fecha prevista y las notas."
                  : "Modifica los datos del hito de facturación."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {!isMetadataOnlyMilestoneEdit && (
              <>
            {/* Type selection */}
            <div className="grid gap-2">
              <Label htmlFor="type">Tipo de hito *</Label>
              <Select
                value={formData.type}
                onValueChange={value => {
                  const nextPercentageBase =
                    value === "percentage" && originalContractAmount
                      ? String(originalContractAmount)
                      : "";
                  if (value === "percentage") setPercentageBaseMode("original");
                  // Clear incompatible fields when type changes
                  setFormData(prev => ({
                    ...prev,
                    type: value as typeof prev.type,
                    ...(value === "fixed_amount" ? { percentage: "", calculationBase: "" } : {}),
                    ...(value === "percentage" ? { amount: "", fixedAmount: "", calculationBase: nextPercentageBase } : {}),
                    ...(value === "monthly_certification" ? { amount: "", fixedAmount: "", percentage: "", calculationBase: "" } : {}),
                  }));
                }}
              >
                <SelectTrigger id="type">
                  <SelectValue placeholder="Selecciona el tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed_amount">Importe fijo</SelectItem>
                  <SelectItem value="percentage">Porcentaje</SelectItem>
                  <SelectItem value="monthly_certification">
                    Certificación mensual
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Label/Concept */}
            <div className="grid gap-2">
              <Label htmlFor="label">Concepto / Label *</Label>
              <Input
                id="label"
                value={formData.label}
                onChange={e => handleInputChange("label", e.target.value)}
                placeholder="Ej: Avance obra, Entrega llaves, etc."
                className={formErrors.label ? "border-red-500" : ""}
              />
              {formErrors.label && (
                <p className="text-sm text-red-500">{formErrors.label}</p>
              )}
            </div>
              </>
            )}

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e => handleInputChange("description", e.target.value)}
                placeholder="Descripción opcional del hito"
              />
            </div>

            {/* Type-specific fields */}
            {!isMetadataOnlyMilestoneEdit && (
              <>
            {formData.type === "fixed_amount" && (
              <div className="grid gap-2">
                <Label htmlFor="amount">Importe fijo (€) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={e => handleInputChange("amount", e.target.value)}
                  placeholder="Ej: 10000"
                  className={formErrors.amount ? "border-red-500" : ""}
                />
                {formErrors.amount && (
                  <p className="text-sm text-red-500">{formErrors.amount}</p>
                )}
              </div>
            )}

            {formData.type === "percentage" && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="percentage">Porcentaje (%) *</Label>
                  <Input
                    id="percentage"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.percentage}
                    onChange={e =>
                      handleInputChange("percentage", e.target.value)
                    }
                    placeholder="Ej: 30"
                    className={formErrors.percentage ? "border-red-500" : ""}
                  />
                  {formErrors.percentage && (
                    <p className="text-sm text-red-500">
                      {formErrors.percentage}
                    </p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="calculationBaseMode">Base de cálculo *</Label>
                  <Select
                    value={percentageBaseMode}
                    onValueChange={value => applyPercentageBaseMode(value as "original" | "effective" | "manual")}
                  >
                    <SelectTrigger id="calculationBaseMode">
                      <SelectValue placeholder="Selecciona la base" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="original">
                        Contrato original · {formatMoney(originalContractAmount)}
                      </SelectItem>
                      <SelectItem value="effective">
                        Importe contractual vigente · {formatMoney(effectiveContractAmount)}
                      </SelectItem>
                      <SelectItem value="manual">Base manual</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="calculationBase"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.calculationBase}
                    onChange={e => {
                      setPercentageBaseMode("manual");
                      handleInputChange("calculationBase", e.target.value);
                    }}
                    placeholder="Ej: 100000"
                    className={formErrors.calculationBase ? "border-red-500" : ""}
                    disabled={percentageBaseMode !== "manual"}
                  />
                  {formErrors.calculationBase && (
                    <p className="text-sm text-red-500">
                      {formErrors.calculationBase}
                    </p>
                  )}
                  {formData.percentage && formData.calculationBase && (
                    <p className="text-sm text-muted-foreground px-3 py-2 bg-muted rounded-md">
                      {Number(formData.percentage).toLocaleString("es-ES")} % sobre base {formatMoney(formData.calculationBase)} = {formatMoney(((parseMoney(formData.calculationBase) ?? 0) * (parseMoney(formData.percentage) ?? 0)) / 100)}
                    </p>
                  )}
                </div>
              </>
            )}

            {formData.type === "monthly_certification" && (
              <div className="space-y-3">
                <div className="grid gap-2">
                  <Label htmlFor="certifiedAmount">Importe certificado (€)</Label>
                  <Input
                    id="certifiedAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.calculatedAmount}
                    onChange={e =>
                      handleInputChange("calculatedAmount", e.target.value)
                    }
                    placeholder="Ej: 10000"
                    className={formErrors.calculatedAmount ? "border-red-500" : ""}
                  />
                  {formErrors.calculatedAmount && (
                    <p className="text-sm text-red-500">
                      {formErrors.calculatedAmount}
                    </p>
                  )}
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      <strong>Nota:</strong> Este tipo permite importe vacío al
                      crear el hito. Cuando se conozca la certificación real,
                      introduce aquí el importe certificado sin cambiar el tipo.
                    </p>
                  </div>
                </div>
              </div>
            )}
              </>
            )}

            {/* Due date */}
            <div className="grid gap-2">
              <Label htmlFor="dueDate">
                Fecha prevista de facturación/emisión
              </Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={e => handleInputChange("dueDate", e.target.value)}
              />
            </div>

            {/* Payment method */}
            {!isMetadataOnlyMilestoneEdit && (
              <>
            <div className="grid gap-2">
              <Label htmlFor="paymentMethod">Forma de pago</Label>
              <Select
                value={formData.paymentMethod}
                onValueChange={value =>
                  handleInputChange("paymentMethod", value)
                }
              >
                <SelectTrigger id="paymentMethod">
                  <SelectValue placeholder="Selecciona forma de pago" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                  <SelectItem value="confirming">Confirming</SelectItem>
                  <SelectItem value="promissory_note">Pagaré</SelectItem>
                  <SelectItem value="cash">Contado</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payment term basis */}
            <div className="grid gap-2">
              <Label htmlFor="paymentTermBasis">Base del plazo</Label>
              <Select
                value={formData.paymentTermBasis}
                onValueChange={value =>
                  handleInputChange("paymentTermBasis", value)
                }
              >
                <SelectTrigger id="paymentTermBasis">
                  <SelectValue placeholder="Selecciona base del plazo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice_date">Fecha de factura</SelectItem>
                  <SelectItem value="end_of_month">Fin de mes</SelectItem>
                  <SelectItem value="client_acceptance">
                    Aceptación cliente
                  </SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payment terms */}
            <div className="grid gap-2">
              <Label htmlFor="paymentTerms">Plazo de pago (días)</Label>
              <Input
                id="paymentTerms"
                type="number"
                min="0"
                value={formData.paymentTerms}
                onChange={e => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  handleInputChange("paymentTerms", value);
                }}
                placeholder="30"
              />
              {formData.dueDate && formData.paymentTerms && formData.paymentTermBasis === "invoice_date" && (
                <p className="text-xs text-muted-foreground">
                  Vencimiento estimado si se emite factura en la fecha prevista:{" "}
                  {(() => {
                    const due = new Date(formData.dueDate);
                    const days = parseInt(formData.paymentTerms) || 0;
                    due.setDate(due.getDate() + days);
                    return due.toLocaleDateString("es-ES");
                  })()}
                </p>
              )}
            </div>
              </>
            )}

            {/* Notes */}
            <div className="grid gap-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={e => handleInputChange("notes", e.target.value)}
                placeholder="Notas adicionales"
              />
            </div>

            {/* Form submit error */}
            {formErrors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{formErrors.submit}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              {formType === "create" ? "Crear hito" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Handle modal open/close
  const openCreateModal = () => {
    setFormType("create");
    setEditingMilestone(null);
    setPercentageBaseMode("original");
    setFormData({
      type: "fixed_amount",
      label: "",
      description: "",
      percentage: "",
      amount: "",
      fixedAmount: "",
      calculationBase: originalContractAmount ? String(originalContractAmount) : "",
      calculatedAmount: "",
      dueDate: "",
      paymentMethod: "transfer",
      paymentTermBasis: "invoice_date",
      paymentTerms: "",
      notes: "",
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (milestone: Milestone) => {
    setFormType("edit");
    setEditingMilestone(milestone);
    setPercentageBaseMode("manual");
    setFormData({
      type: milestone.type,
      label: milestone.label || "",
      description: milestone.description || "",
      percentage:
        milestone.percentage !== null ? String(milestone.percentage) : "",
      amount:
        milestone.type === "monthly_certification"
          ? ""
          : milestone.fixedAmount || "",
      fixedAmount:
        milestone.type === "monthly_certification"
          ? ""
          : milestone.fixedAmount || "",
      calculationBase: milestone.fixedAmount || "",
      calculatedAmount: milestone.calculatedAmount || "",
      dueDate: formatDateInputValue(milestone.dueDate),
      paymentMethod: milestone.paymentMethod || "transfer",
      paymentTermBasis: milestone.paymentTermBasis || "invoice_date",
      paymentTerms: milestone.paymentTerms || "",
      notes: milestone.notes || "",
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const renderEditButton = (milestone: Milestone) => {
    if (!canEditBillingMilestone(milestone)) return null;
    return (
      <Button
        variant="ghost"
        size="sm"
        aria-label="Editar hito de facturación"
        onClick={() => openEditModal(milestone)}
        disabled={canWriteMilestoneEconomics && effectiveContractAmountUnavailable}
        className="h-8 px-2"
      >
        <Edit className="w-3.5 h-3.5" />
      </Button>
    );
  };

  const renderBillingAllocationSummary = () => {
    if (!isManager) return null;

    if (ceilingQuery.isLoading) {
      return (
        <Card>
          <CardContent className="py-5">
            <div className="text-sm text-muted-foreground">
              Calculando distribución...
            </div>
          </CardContent>
        </Card>
      );
    }

    if (ceilingQuery.isError) {
      return (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-5">
            <div className="flex items-start gap-2 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>No se pudo calcular la distribución de facturación.</span>
            </div>
          </CardContent>
        </Card>
      );
    }

    const summaryState =
      remainingToAllocateNumber === null
        ? "unknown"
        : remainingToAllocateNumber < 0
          ? "over_allocated"
          : remainingToAllocateNumber === 0
            ? "complete"
            : "pending";
    const stateCopy = {
      pending: "Pendiente de distribuir",
      complete: "Contrato completamente distribuido",
      over_allocated: "Importe comprometido superior al contrato",
      unknown: "Distribución no disponible",
    }[summaryState];
    const stateClassName = {
      pending: "border-amber-200 bg-amber-50 text-amber-800",
      complete: "border-emerald-200 bg-emerald-50 text-emerald-700",
      over_allocated: "border-red-200 bg-red-50 text-red-700",
      unknown: "border-slate-200 bg-slate-50 text-slate-700",
    }[summaryState];

    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <CardTitle className="flex items-center gap-2 text-base text-primary">
              <CreditCard className="h-4 w-4" />
              Distribución de facturación
            </CardTitle>
            <Badge variant="outline" className={stateClassName}>
              {stateCopy}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-border p-3">
              <div className="text-xs font-medium uppercase text-muted-foreground">
                Importe contractual vigente
              </div>
              <div className="mt-1 text-lg font-semibold">
                {formatMoney(effectiveContractAmount)}
              </div>
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="text-xs font-medium uppercase text-muted-foreground">
                Comprometido en hitos activos
              </div>
              <div className="mt-1 text-lg font-semibold">
                {formatMoney(committedAmount)}
              </div>
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="text-xs font-medium uppercase text-muted-foreground">
                Pendiente de distribuir
              </div>
              <div className={summaryState === "over_allocated" ? "mt-1 text-lg font-semibold text-red-700" : "mt-1 text-lg font-semibold"}>
                {formatMoney(remainingToAllocate)}
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>Contrato original: {formatMoney(originalContractAmount)}</span>
            <span>Ampliaciones aprobadas: {formatMoney(approvedIncreaseAmount)}</span>
            <span>Reducciones aprobadas: {formatMoney(approvedReductionAmount)}</span>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderMobileMilestoneActions = (milestone: Milestone) => {
    if (!isManager) return null;

    return (
      <div className="mt-3 border-t border-border pt-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Acciones
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {renderStatusActions(milestone)}
          {renderEditButton(milestone)}
        </div>
      </div>
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingMilestone(null);
    setFormType("create");
  };

  // Handle form input changes
  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (isMetadataOnlyMilestoneEdit) {
      setFormErrors(errors);
      return true;
    }

    if (!formData.label || formData.label.trim() === "") {
      errors.label = "Concepto/label es obligatorio";
    }

    const isEdit = formType === "edit" && !!editingMilestone;
    const isEmpty = (val: any) =>
      val === "" || val === null || val === undefined;
    const toNumber = (val: any) => {
      if (isEmpty(val)) return null;
      const num = Number(val);
      return Number.isFinite(num) ? num : null;
    };
    const isValidPositiveAmount = (val: any) => {
      const num = toNumber(val);
      return num !== null && num > 0;
    };
    const isValidPercentage = (val: any) => {
      const num = toNumber(val);
      return num !== null && num > 0 && num <= 100;
    };
    const stringValue = (val: any) => (isEmpty(val) ? "" : String(val));

    const typeChanged = isEdit && editingMilestone?.type !== formData.type;
    const amountValue =
      formData.type === "monthly_certification"
        ? formData.calculatedAmount
        : formData.amount || formData.fixedAmount;
    const originalAmount =
      editingMilestone?.fixedAmount || editingMilestone?.calculatedAmount || "";
    const amountChanged =
      isEdit && stringValue(amountValue) !== stringValue(originalAmount);
    const percentageChanged =
      isEdit &&
      stringValue(formData.percentage) !==
        stringValue(editingMilestone?.percentage);

    if (formData.type === "fixed_amount") {
      if (formType === "create" || typeChanged || amountChanged) {
        if (!isValidPositiveAmount(amountValue)) {
          errors.amount =
            "Importe es obligatorio y debe ser mayor a 0 para hitos de tipo Importe fijo";
        }
      }
    } else if (formData.type === "percentage") {
      if (formType === "create" || typeChanged || percentageChanged) {
        if (!isValidPercentage(formData.percentage)) {
          errors.percentage =
            "Porcentaje es obligatorio y debe ser mayor a 0 y menor o igual a 100";
        }
      }

      if (!isValidPositiveAmount(formData.calculationBase)) {
        errors.calculationBase =
          "La base de cálculo es obligatoria y debe ser mayor a 0 para hitos porcentuales";
      }
    } else if (formData.type === "monthly_certification") {
      if (
        formData.calculatedAmount &&
        !isValidPositiveAmount(formData.calculatedAmount)
      ) {
        errors.calculatedAmount =
          "El importe certificado debe ser mayor a 0";
      }
    }

    // Validate that contractual milestones don't exceed the effective contract amount.
    if (
      effectiveContractAmountNumber !== null &&
      effectiveContractAmountNumber > 0 &&
      (formData.type === "fixed_amount" || formData.type === "percentage")
    ) {
      const price = effectiveContractAmountNumber;
      const editingId = formType === "edit" ? editingMilestone?.id : undefined;
      // Sum planned obligations only. Invoice totals may include tax and are not plan amounts.
      const otherContractualMilestones = (milestonesQuery.data || [])
        .filter((m: Milestone) =>
          m.id !== editingId &&
          (m.type === "fixed_amount" || m.type === "percentage")
        );
      const otherContractualAmounts: Array<number | null> = otherContractualMilestones.map((milestone: Milestone) =>
        resolvePlannedBillingMilestoneAmount(milestone)
      );
      const hasUnverifiablePlan = otherContractualAmounts.some(amount => amount === null);
      const otherContractualTotal = hasUnverifiablePlan
        ? null
        : otherContractualAmounts.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);

      if (otherContractualTotal === null) {
        errors.amount = errors.amount ||
          "No se puede verificar el importe planificado de todos los hitos existentes; revisa los datos económicos antes de continuar.";
      }

      let thisAmount = 0;
      if (formData.type === "fixed_amount") {
        thisAmount = parseFloat(formData.amount || formData.fixedAmount || "0") || 0;
      } else if (formData.type === "percentage") {
        const pct = parseFloat(formData.percentage || "0") || 0;
        const base = parseFloat(formData.calculationBase || "0") || 0;
        thisAmount = Math.round((base * pct) / 100 * 100) / 100;
      }

      if (otherContractualTotal !== null && otherContractualTotal + thisAmount > price + 0.01) {
        errors.amount = errors.amount || errors.percentage ||
          `La suma de hitos contractuales (${(otherContractualTotal + thisAmount).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €) supera el importe contractual vigente (${price.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €). Registra una modificación contractual o ajusta los hitos editables.`;
        if (formData.type === "fixed_amount") errors.amount = errors.amount;
        if (formData.type === "percentage") errors.percentage = errors.amount;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit (create or update)
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (isMetadataOnlyMilestoneEdit && editingMilestone) {
      try {
        const originalDueDate = formatDateInputValue(editingMilestone.dueDate);
        const updatePayload = {
          id: editingMilestone.id,
          description: formData.description === "" ? null : formData.description,
          ...(formData.dueDate !== originalDueDate
            ? { dueDate: formData.dueDate ? dateInputToUtcDate(formData.dueDate) : null }
            : {}),
          notes: formData.notes === "" ? null : formData.notes,
        };
        await updateMutation.mutateAsync(updatePayload);
        await refreshBillingViews();
        toast.success("Hito actualizado exitosamente");
        closeModal();
      } catch (error: any) {
        const errorMessage = error?.message || "Error al guardar el hito";
        toast.error(errorMessage);
        setFormErrors({ submit: errorMessage });
      }
      return;
    }

    // FIN-04: for fixed_amount type, use formData.amount (the visible field) as the
    // authoritative value. Do NOT send calculationBase for fixed_amount — it was
    // initialized to the old fixedAmount and would silently overwrite the new value
    // in the backend (backend processes: amount → fixedAmount → calculationBase, last wins).
    const isFixedAmount = formData.type === "fixed_amount";
    const isPercentage = formData.type === "percentage";
    const isMonthlyCertification = formData.type === "monthly_certification";
    const percentageBase = isPercentage && formData.calculationBase
      ? String(formData.calculationBase)
      : undefined;
    const submitData: any = {
      installationId: installation.id,
      type: formData.type,
      label: formData.label,
      description: formData.description || undefined,
      percentage: isPercentage && formData.percentage ? Number(formData.percentage) : undefined,
      amount: isFixedAmount ? (formData.amount || undefined) : undefined,
      fixedAmount: isFixedAmount ? (formData.amount || undefined) : undefined,
      calculationBase: percentageBase,
      calculatedAmount: isMonthlyCertification
        ? (formData.calculatedAmount || undefined)
        : formData.calculatedAmount || undefined,
      dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
      paymentMethod: formData.paymentMethod as any,
      paymentTermBasis: formData.paymentTermBasis as any,
      paymentTerms: formData.paymentTerms || undefined,
      notes: formData.notes || undefined,
    };

    const toComparableCents = (value: unknown) => {
      const parsed = parseMoney(value);
      return parsed === null ? null : Math.round(parsed * 100);
    };
    const sameEconomicValue = (left: unknown, right: unknown) =>
      toComparableCents(left) === toComparableCents(right);

    try {
      if (formType === "create") {
        await createMutation.mutateAsync(submitData);
        // Refetch milestones
        await refreshBillingViews();
        toast.success("Hito creado exitosamente");
      } else if (formType === "edit" && editingMilestone) {
        const typeChanged = editingMilestone.type !== formData.type;
        const percentageChanged =
          formData.type === "percentage" &&
          !sameEconomicValue(editingMilestone.percentage, formData.percentage);
        const percentageBaseChanged =
          formData.type === "percentage" &&
          !sameEconomicValue(editingMilestone.fixedAmount, formData.calculationBase);
        const fixedAmountChanged =
          formData.type === "fixed_amount" &&
          !sameEconomicValue(editingMilestone.fixedAmount, formData.amount || formData.fixedAmount);
        const monthlyAmountChanged =
          formData.type === "monthly_certification" &&
          !sameEconomicValue(editingMilestone.calculatedAmount, formData.calculatedAmount);
        const economicChange =
          typeChanged || percentageChanged || percentageBaseChanged || fixedAmountChanged || monthlyAmountChanged;
        const confirmEconomicReadyToPending = editingMilestone.status === "ready_to_invoice" && economicChange
          ? window.confirm("Corregir económicamente un hito listo para facturar lo devolverá automáticamente a pendiente. ¿Confirmas la corrección?")
          : undefined;
        if (editingMilestone.status === "ready_to_invoice" && economicChange && !confirmEconomicReadyToPending) return;

        const updatePayload: any = {
          id: editingMilestone.id,
          label: submitData.label,
          description: formData.description === "" ? null : submitData.description,
          dueDate: formData.dueDate ? submitData.dueDate : null,
          paymentMethod: submitData.paymentMethod,
          paymentTermBasis: submitData.paymentTermBasis,
          paymentTerms: submitData.paymentTerms,
          notes: formData.notes === "" ? null : submitData.notes,
        };
        if (typeChanged) updatePayload.type = submitData.type;
        if (typeChanged || percentageChanged || percentageBaseChanged) {
          updatePayload.percentage = submitData.percentage;
          updatePayload.calculationBase = submitData.calculationBase;
        }
        if (typeChanged || fixedAmountChanged) {
          updatePayload.amount = submitData.amount;
          updatePayload.fixedAmount = submitData.fixedAmount;
        }
        if (typeChanged || monthlyAmountChanged) {
          updatePayload.calculatedAmount = submitData.calculatedAmount;
        }
        if (confirmEconomicReadyToPending !== undefined) {
          updatePayload.confirmEconomicReadyToPending = confirmEconomicReadyToPending;
        }

        await updateMutation.mutateAsync(updatePayload);
        // Refetch milestones
        await refreshBillingViews();
        toast.success("Hito actualizado exitosamente");
      }
      closeModal();
    } catch (error: any) {
      const errorMessage = error?.message || "Error al guardar el hito";
      toast.error(errorMessage);
      setFormErrors({
        submit: errorMessage,
      });
    }
  };

  if (milestonesQuery.isLoading) {
    return (
      <div className="p-8 text-center">
        <span className="text-muted-foreground">
          Cargando datos de facturación...
        </span>
      </div>
    );
  }

  if (milestonesQuery.isError) {
    return (
      <div className="p-8 text-center">
        <span className="text-red-500">
          Error al cargar los hitos de facturación
        </span>
      </div>
    );
  }

  const milestones = milestonesQuery.data || [];
  const cancelledMilestonesCount = milestones.filter(
    (m: Milestone) => m.status === "cancelled"
  ).length;

  return (
    <div className="space-y-6 overflow-visible pb-24 sm:pb-0">
      {/* Installation Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Estado Contrato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="text-lg font-semibold">
                {contractStatus === "contract_signed"
                  ? "Firmado"
                  : contractStatus === "contract_not_required"
                    ? "No requerido"
                    : contractStatus === "pending"
                      ? "Pendiente"
                      : "No configurado"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Configuración Facturación
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-muted-foreground" />
              <span className="text-lg font-semibold">
                {billingConfigStatus === "billing_validated"
                  ? "Validada"
                  : billingConfigStatus === "billing_provisional"
                    ? "Provisional"
                    : billingConfigStatus === "pending"
                      ? "Pendiente"
                      : "No configurada"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Mantenimiento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-lg font-semibold">
                {hasContractedMaintenance ? "Sí" : "No"}
              </span>
            </div>
            {hasContractedMaintenance && (
              <p className="mt-1 text-sm text-muted-foreground">
                Estado pago: {maintenancePaymentStatus === "payment_overdue"
                  ? "Atrasado"
                  : maintenancePaymentStatus === "payment_pending"
                    ? "Pendiente"
                    : maintenancePaymentStatus === "account_current"
                      ? "Cuenta corriente"
                      : "Desconocido"}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerts Banners */}
      <div className="space-y-2">
        {ceilingQuery.isLoading && (
          <div className="p-4 bg-muted border border-border rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              Cargando importe contractual vigente...
            </p>
          </div>
        )}
        {ceilingQuery.isError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">
              No se pudo obtener el importe contractual vigente.
            </p>
          </div>
        )}
        {hasPendingContract && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-amber-800">
                Contrato Pendiente
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                Los hitos creados serán provisionales hasta validar el contrato.
                Revisa los importes antes de avisar a Contabilidad o iniciar la
                instalación.
              </p>
            </div>
          </div>
        )}

        {showProvisionalBanner && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-800">
                {billingConfigStatus === "billing_provisional"
                  ? "Facturación Provisional"
                  : "Advertencia de Facturación"}
              </h4>
              <p className="text-sm text-blue-700 mt-1">
                {billingConfigStatus === "billing_provisional"
                  ? "Esta instalación utiliza facturación mensual por certificación. Los hitos requieren validación manual."
                  : "Existen aspectos pendientes en la configuración de facturación que requieren atención."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contract and adjustments */}
      {isManager && (
        <ContractAdjustmentsSection
          installation={installation}
          contractDocument={getContractDocument()}
          contractFileInputRef={contractFileInputRef}
          onContractFileSelected={handleContractFileSelected}
          contractUploadDisabled={loadingAction === "upload_contract"}
          contractUploadAllowed={installation.contractId == null && installation.contractDocumentId == null}
        />
      )}

      {/* Billing Documents */}
      {renderBillingDocuments()}

      {/* Billing Allocation Summary */}
      {renderBillingAllocationSummary()}

      {/* Milestones Summary */}
      {milestones.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <FileText className="w-5 h-5" />
              Resumen de Hitos por Estado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {milestones.filter((m: Milestone) => m.status === "pending")
                .length > 0 && (
                <Badge variant="outline" className="gap-2">
                  <Clock className="w-3 h-3" />
                  Pendientes:{" "}
                  {
                    milestones.filter((m: Milestone) => m.status === "pending")
                      .length
                  }
                </Badge>
              )}
              {milestones.filter(
                (m: Milestone) => m.status === "ready_to_invoice"
              ).length > 0 && (
                <Badge variant="outline" className="gap-2">
                  <FileText className="w-3 h-3" />
                  Listos para facturar:{" "}
                  {
                    milestones.filter(
                      (m: Milestone) => m.status === "ready_to_invoice"
                    ).length
                  }
                </Badge>
              )}
              {milestones.filter((m: Milestone) => m.status === "invoiced")
                .length > 0 && (
                <Badge variant="outline" className="gap-2">
                  <CheckCircle2 className="w-3 h-3" />
                  Facturados:{" "}
                  {
                    milestones.filter((m: Milestone) => m.status === "invoiced")
                      .length
                  }
                </Badge>
              )}
              {milestones.filter((m: Milestone) => m.status === "sent_to_client").length > 0 && (
                <Badge variant="outline" className="gap-2">
                  <CheckCircle2 className="w-3 h-3" />
                  Enviados al cliente: {milestones.filter((m: Milestone) => m.status === "sent_to_client").length}
                </Badge>
              )}
              {milestones.filter(
                (m: Milestone) => m.status === "accounting_notified"
              ).length > 0 && (
                <Badge variant="outline" className="gap-2">
                  <Info className="w-3 h-3" />
                  Notificado contabilidad:{" "}
                  {
                    milestones.filter(
                      (m: Milestone) => m.status === "accounting_notified"
                    ).length
                  }
                </Badge>
              )}
              {milestones.filter(
                (m: Milestone) => isInvoiceCollectionPending(m) && !isInvoicePaidPendingEconomicReview(m)
              ).length > 0 && (
                <Badge variant="outline" className="gap-2">
                  <Clock className="w-3 h-3" />
                  Cobro pendiente:{" "}
                  {
                    milestones.filter(
                      (m: Milestone) => isInvoiceCollectionPending(m) && !isInvoicePaidPendingEconomicReview(m)
                    ).length
                  }
                </Badge>
              )}
              {milestones.filter((m: Milestone) => isInvoicePaidPendingEconomicReview(m)).length > 0 && (
                <Badge variant="outline" className="gap-2 whitespace-normal text-left">
                  <CheckCircle2 className="w-3 h-3 shrink-0" />
                  Factura cobrada; hito pendiente de revisión económica:{" "}
                  {milestones.filter((m: Milestone) => isInvoicePaidPendingEconomicReview(m)).length}
                </Badge>
              )}
              {milestones.filter((m: Milestone) => isInvoiceCollectionComplete(m) && !isInvoicePaidPendingEconomicReview(m))
                .length > 0 && (
                <Badge variant="outline" className="gap-2">
                  <CheckCircle2 className="w-3 h-3" />
                  Cobrados:{" "}
                  {
                    milestones.filter(
                      (m: Milestone) => isInvoiceCollectionComplete(m) && !isInvoicePaidPendingEconomicReview(m)
                    ).length
                  }
                </Badge>
              )}
              {milestones.filter((m: Milestone) => m.status === "cancelled")
                .length > 0 && (
                <Badge variant="outline" className="gap-2">
                  <FileText className="w-3 h-3" />
                  Cancelados:{" "}
                  {
                    milestones.filter(
                      (m: Milestone) => m.status === "cancelled"
                    ).length
                  }
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions Section */}
      <Card>
        <CardContent className="py-4 sm:py-6">
          {/* Create button (only for managers) */}
          {renderCreateButton()}
        </CardContent>
      </Card>

      {/* Milestones Table */}
      <Card className="overflow-hidden">
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-primary">
                <FileText className="w-5 h-5" />
                Historial de Hitos de Facturación
              </CardTitle>
              {installation.clientName && (
                <p className="text-sm text-muted-foreground mt-1">
                  Cliente: <span className="font-medium">{installation.clientName}</span>
                  {installation.clientId && (
                    <> · Expediente: <span className="font-medium">{installation.clientId}</span></>
                  )}
                </p>
              )}
            </div>
            {isManager && (
              <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
                <div className="text-right">
                  <Label htmlFor="show-cancelled-milestones" className="text-sm font-medium">
                    Mostrar cancelados
                  </Label>
                  {showCancelled && cancelledMilestonesCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Cancelados: {cancelledMilestonesCount}
                    </p>
                  )}
                </div>
                <Switch
                  id="show-cancelled-milestones"
                  checked={showCancelled}
                  onCheckedChange={setShowCancelled}
                  aria-label="Mostrar hitos cancelados"
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-3 p-3 md:hidden">
            {milestones.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No hay hitos de facturación para esta instalación
              </div>
            ) : (
              milestones.map((milestone: Milestone) => (
                <div key={milestone.id} className="rounded-md border border-border bg-background p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <div className="font-medium break-words [overflow-wrap:anywhere]">
                        {milestone.label || "Sin concepto"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {getMilestoneTypeLabel(milestone.type)}
                      </div>
                    </div>
                    <div className="shrink-0">{getMilestoneStatusBadge(milestone, isInvoicePaidPendingEconomicReview(milestone))}</div>
                  </div>

                  <div className="mt-3 grid gap-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Planificado</span>
                      <span className="text-right font-medium">{getMilestoneAmountLabel(milestone)}</span>
                    </div>
                    {hasVerifiedBillingInvoiceIdentity(milestone) && milestone.invoiceAmount != null && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Factura exigible</span><span className="text-right">{formatMoney(milestone.invoiceAmount)}</span></div>}
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Vencimiento</span>
                      <span className="text-right">
                        {renderMilestoneDueState(milestone)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Factura / Cobro</span>
                      <span className="max-w-[60%] text-right text-xs text-gray-600 break-words [overflow-wrap:anywhere]">
                        {isInvoiceCollectionComplete(milestone) || isInvoicePaidPendingEconomicReview(milestone) ? (
                          <div className="space-y-0.5">
                            <div>{getCollectedPaymentLabel(milestone)}</div>
                            {milestone.financialCostAmount ? (
                              <div className="text-[11px] text-muted-foreground">
                                Coste financiero: {formatMoney(milestone.financialCostAmount)}
                                {((milestone.bankCommissionAmount && milestone.bankCommissionAmount !== "0.00") || (milestone.bankInterestAmount && milestone.bankInterestAmount !== "0.00")) ? (
                                  <span> (Comisión: {formatMoney(milestone.bankCommissionAmount || "0.00")}, Intereses: {formatMoney(milestone.bankInterestAmount || "0.00")})</span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : milestone.invoiceNumber ? (
                          `#${milestone.invoiceNumber}`
                        ) : (
                          "—"
                        )}
                      </span>
                    </div>
                  </div>
                  {renderEconomicReviewWarning(milestone)}
                  {renderMobileMilestoneActions(milestone)}
                  <div className="mt-3">
                    {renderAccountingNoticeSummary(milestone, true)}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden w-full overflow-x-auto overscroll-x-contain md:block">
            <table className="min-w-[1100px] text-left text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-6 py-3">Estado</th>
                  <th className="px-6 py-3">Concepto</th>
                  <th className="px-6 py-3">Tipo</th>
                  <th className="px-6 py-3">Desglose</th>
                  <th className="px-6 py-3">Importe</th>
                  <th className="px-6 py-3">Vencimiento</th>
                  <th className="px-6 py-3">Forma de Pago</th>
                  <th className="px-6 py-3">Plazo de Pago</th>
                  <th className="px-6 py-3">Aviso Contabilidad</th>
                  <th className="px-6 py-3">Factura / Cobro</th>
                  <th className="px-6 py-3">Acciones</th>
                  <th className="px-6 py-3">Edición</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {milestones.length === 0 ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-6 py-8 text-center text-muted-foreground"
                    >
                      No hay hitos de facturación para esta instalación
                    </td>
                  </tr>
                ) : (
                  milestones.map((milestone: Milestone) => (
                    <React.Fragment key={milestone.id}>
                      <tr className="hover:bg-muted/30">
                        <td className="px-6 py-4">
                          {getMilestoneStatusBadge(milestone, isInvoicePaidPendingEconomicReview(milestone))}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium break-words [overflow-wrap:anywhere]">
                            {milestone.label || "—"}
                          </div>
                          {milestone.description && (
                            <div className="text-xs text-muted-foreground mt-1 break-words">
                              {milestone.description}
                            </div>
                          )}
                          {renderEconomicReviewWarning(milestone)}
                        </td>
                        <td className="px-6 py-4">
                          {getMilestoneTypeLabel(milestone.type)}
                        </td>
                        <td className="px-6 py-4">
                          {getMilestoneBaseLabel(milestone)}
                        </td>
                        <td className="px-6 py-4">
                          <div>Planificado: {getMilestoneAmountLabel(milestone)}</div>
                          {hasVerifiedBillingInvoiceIdentity(milestone) && milestone.invoiceAmount != null && <div className="text-xs text-muted-foreground">Factura exigible: {formatMoney(milestone.invoiceAmount)}</div>}
                        </td>
                        <td className="px-6 py-4">
                          {renderMilestoneDueState(milestone)}
                        </td>
                        <td className="px-6 py-4">
                          {getPaymentMethodLabel(
                            milestone.effectivePaymentMethod ?? milestone.paymentMethod,
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {getPaymentTermLabel(milestone.paymentTermBasis)}
                          {milestone.paymentTerms && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {milestone.paymentTerms}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-[260px]">
                            {renderAccountingNoticeSummary(milestone, true)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {milestone.invoiceNumber || isInvoiceCollectionComplete(milestone) || isInvoicePaidPendingEconomicReview(milestone) ? (
                            <div className="max-w-[220px] space-y-1 break-words [overflow-wrap:anywhere]">
                              {milestone.invoiceNumber && (
                                <div className="text-sm font-medium">
                                  #{milestone.invoiceNumber}
                                </div>
                              )}
                              {milestone.invoiceDate && (
                                <div className="text-xs text-muted-foreground">
                                  {new Date(
                                    milestone.invoiceDate
                                  ).toLocaleDateString("es-ES")}
                                </div>
                              )}
                              {(isInvoiceCollectionComplete(milestone) || isInvoicePaidPendingEconomicReview(milestone)) && (
                                <div className="text-xs text-gray-500 space-y-0.5">
                                  <div>{getCollectedPaymentLabel(milestone)}</div>
                                  {milestone.financialCostAmount ? (
                                    <div className="text-[11px] text-muted-foreground">
                                      Coste financiero: {formatMoney(milestone.financialCostAmount)}
                                      {((milestone.bankCommissionAmount && milestone.bankCommissionAmount !== "0.00") || (milestone.bankInterestAmount && milestone.bankInterestAmount !== "0.00")) ? (
                                        <span> (Comisión: {formatMoney(milestone.bankCommissionAmount || "0.00")}, Intereses: {formatMoney(milestone.bankInterestAmount || "0.00")})</span>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {renderStatusActions(milestone)}
                        </td>
                        <td className="px-6 py-4">
                          {renderEditButton(milestone)}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Form Modal */}
      {renderFormModal()}

      {/* Confirming / Regular settlement dialog */}
      {actionMilestone && statusDialog === "confirming_settlement" && (
        <Dialog
          open={true}
          onOpenChange={open => {
            if (!open) closeStatusDialog();
          }}
        >
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>
                {collectionMethod === "confirming"
                  ? "Registrar cobro Confirming"
                  : "Registrar cobro"}
              </DialogTitle>
              <DialogDescription>
                {collectionMethod === "confirming"
                  ? "El importe satisfecho del cliente es el bruto. La comisión y los intereses se registran como coste financiero y el neto bancario se calcula automáticamente."
                  : "Introduce la fecha y el importe cobrado para este hito."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="font-medium">
                  {actionMilestone.label || actionMilestone.description || "Hito de facturación"}
                </div>
                <div className="text-muted-foreground">
                  Cliente: {installation.clientName || installation.clientId || installation.id}
                </div>
                {(() => {
                  const obligationRaw = getCollectionObligationAmount(actionMilestone);
                  const obligation = parseMoney(obligationRaw);
                  const previous = parseMoney(actionMilestone.collectedAmount) ?? 0;
                  const remaining = parseMoney(actionMilestone.balanceAmount);
                  const operation = parseCollectionPaymentAmount(grossPaymentAmount);
                  const balanceCents = remaining === null || operation === null
                    ? null
                    : Math.round(remaining * 100) - Math.round(operation * 100);
                  const hasInvoiceAmount = obligationRaw !== null && obligationRaw !== undefined;
                  return <div className="mt-2 space-y-1">
                    <div>Importe aprobado: {formatMoney(getApprovedMilestoneAmount(actionMilestone))}</div>
                    <div>Factura exigible: {hasInvoiceAmount ? formatMoney(obligationRaw) : "Factura no registrada"}</div>
                    <div>Cobrado antes: {formatMoney(previous)}</div>
                    <div>Importe de esta operación: {formatMoney(operation)}</div>
                    <div data-testid="billingtab-collection-balance">
                      {actionMilestone.invoiceFiscalVerified !== true
                        ? "No se puede registrar el cobro: falta revisar el desglose fiscal de la factura."
                        : obligation === null || obligation <= 0 || remaining === null
                        ? "No se puede validar: no existe saldo de una factura verificable."
                        : balanceCents === null
                        ? "Introduce un importe válido (máximo dos decimales) para calcular el saldo."
                        : balanceCents > 0
                          ? `Saldo posterior: ${formatMoney(balanceCents / 100)}. Se registrará como cobro parcial y la factura seguirá pendiente.`
                          : balanceCents < 0
                            ? "El cobro supera el importe exigible; el excedente no se aplicará automáticamente."
                            : actionMilestone.invoiceEconomicReviewRequired
                              ? "Factura cobrada; el hito seguirá pendiente de revisión económica."
                              : "Este cobro liquida la factura."}
                    </div>
                  </div>;
                })()}
              </div>

              {actionMilestone.collectionEvents && actionMilestone.collectionEvents.length > 0 ? (
                <div className="rounded-md border p-3 text-sm" data-testid="collection-history">
                  <div className="mb-2 font-medium">Cobros registrados</div>
                  <ul className="space-y-1">
                    {actionMilestone.collectionEvents.map((event, index) => (
                      <li key={String(event.id ?? index)}>
                        {String(event.eventType) === "reversal" ? "Reverso" : "Cobro"} · {formatMoney(event.amount)} · {String(event.occurredAt ?? "Fecha no disponible").slice(0, 10)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="grid gap-2">
                <Label htmlFor="collectionMethod">Método real del cobro *</Label>
                <Select value={collectionMethod} onValueChange={setCollectionMethod}>
                  <SelectTrigger id="collectionMethod"><SelectValue placeholder="Selecciona el método" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transfer">Transferencia</SelectItem>
                    <SelectItem value="confirming">Confirming</SelectItem>
                    <SelectItem value="promissory_note">Pagaré</SelectItem>
                    <SelectItem value="cash">Contado</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="confirmingCollectedDate">Fecha de cobro *</Label>
                  <Input
                    id="confirmingCollectedDate"
                    type="date"
                    value={collectedDate}
                    onChange={event => setCollectedDate(event.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="confirmingGrossPaymentAmount">Importe satisfecho bruto (€) *</Label>
                  <Input
                    id="confirmingGrossPaymentAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={grossPaymentAmount}
                    onChange={event => setGrossPaymentAmount(event.target.value)}
                  />
                </div>
                <div className="grid gap-2 sm:col-span-2">
                  <Label htmlFor="collectionReference">Referencia bancaria (opcional)</Label>
                  <Input id="collectionReference" value={collectionReference} maxLength={255} onChange={event => setCollectionReference(event.target.value)} />
                </div>
                {collectionMethod === "confirming" ? (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="confirmingBankCommissionAmount">Comisión bancaria (€)</Label>
                      <Input
                        id="confirmingBankCommissionAmount"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={bankCommissionAmount}
                        onChange={event => setBankCommissionAmount(event.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="confirmingBankInterestAmount">Intereses (€)</Label>
                      <Input
                        id="confirmingBankInterestAmount"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={bankInterestAmount}
                        onChange={event => setBankInterestAmount(event.target.value)}
                      />
                    </div>
                  </>
                ) : null}
              </div>

              {collectionMethod === "confirming" ? (
                <div className="grid gap-2 rounded-md border p-3 text-sm sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Coste financiero:</span>{" "}
                    <span className="font-medium">
                      {formatMoney(
                        (parseMoney(bankCommissionAmount) ?? 0) +
                          (parseMoney(bankInterestAmount) ?? 0),
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Neto recibido banco:</span>{" "}
                    <span className="font-semibold">
                      {getConfirmingNetAmount() === null
                        ? "—"
                        : formatMoney(getConfirmingNetAmount())}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeStatusDialog} disabled={loadingAction !== null}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmingSettlementSubmit}
                disabled={loadingAction === `mark_collected:${actionMilestone.id}` || !collectedDate || actionMilestone.invoiceFiscalVerified !== true || (() => {
                  const gross = parseCollectionPaymentAmount(grossPaymentAmount);
                  const balance = parseMoney(actionMilestone.balanceAmount);
                  return gross === null || gross <= 0 || balance === null || balance <= 0 ||
                    Math.round(gross * 100) > Math.round(balance * 100);
                })()}
              >
                {loadingAction === `mark_collected:${actionMilestone.id}`
                  ? "Guardando..."
                  : (parseMoney(actionMilestone.balanceAmount) ?? 0) - (parseCollectionPaymentAmount(grossPaymentAmount) ?? 0) > 0.005
                    ? "Registrar cobro parcial"
                    : "Registrar cobro"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Generic status confirmation dialog */}
      {pendingStatusAction &&
        actionMilestone &&
        statusDialog === "confirm_status" &&
        pendingStatusAction.action !== "notify_accounting" && (
          <Dialog
            open={true}
            onOpenChange={open => {
              if (!open) closeStatusDialog();
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirmar cambio de estado</DialogTitle>
                <DialogDescription>
                  Vas a cambiar este hito a {pendingStatusAction.newStatus}.
                  ¿Estás seguro?
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-4 text-sm">
                {renderMonthlyCertificationWarning(actionMilestone)}
                <div>
                  <span className="font-medium">Instalación:</span>{" "}
                  {installation.clientName ||
                    installation.clientId ||
                    installation.id}
                </div>
                <div>
                  <span className="font-medium">Hito:</span>{" "}
                  {actionMilestone.label || actionMilestone.description || "—"}
                </div>
                <div>
                  <span className="font-medium">Planificado:</span>{" "}
                  {getMilestoneAmountLabel(actionMilestone)}
                </div>
                <div>
                  <span className="font-medium">Estado actual:</span>{" "}
                  {getMilestoneStatusLabel(actionMilestone.status, actionMilestone)}
                </div>
                <div>
                  <span className="font-medium">Nuevo estado:</span>{" "}
                  {pendingStatusAction.newStatus}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeStatusDialog}>
                  Cancelar
                </Button>
                <Button
                  onClick={async () => {
                    const currentAction = pendingStatusAction;
                    closeStatusDialog();
                    await runStatusMutation(
                      currentAction.action,
                      currentAction.milestone
                    );
                  }}
                >
                  Confirmar cambio
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

      {/* Rich confirm dialog for notify_accounting */}
      {pendingStatusAction &&
        actionMilestone &&
        statusDialog === "confirm_status" &&
        pendingStatusAction.action === "notify_accounting" && (
          <Dialog
            open={true}
            onOpenChange={open => {
              if (!open) closeStatusDialog();
            }}
          >
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Notificar a Contabilidad</DialogTitle>
                <DialogDescription>
                  Se va a notificar este hito al departamento de Contabilidad.
                  Revisa los datos antes de confirmar.
                </DialogDescription>
              </DialogHeader>

              {!installation.clientDocument && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                  <span className="font-semibold">⚠️ Faltan datos fiscales del cliente (CIF/DNI/NIF):</span> revisar antes de facturar.
                </div>
              )}

              <div className="space-y-3 py-2 text-sm max-h-[50vh] overflow-y-auto">
                {renderMonthlyCertificationWarning(actionMilestone)}

                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div className="col-span-2 border-b pb-1 mb-1">
                    <span className="font-semibold">Datos del cliente</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Cliente:</span>
                  </div>
                  <div>{installation.clientName || "—"}</div>
                  <div>
                    <span className="font-medium text-gray-500">CIF/NIF:</span>
                  </div>
                  <div className={!installation.clientDocument ? "text-amber-600 font-medium" : ""}>
                    {installation.clientDocument || "—"}
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Expediente:</span>
                  </div>
                  <div>{installation.clientId || "—"}</div>

                  <div className="col-span-2 border-b pb-1 mb-1 mt-1">
                    <span className="font-semibold">Instalación / Hito</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Instalación:</span>
                  </div>
                  <div>{installation.workDescription || installation.clientId || installation.id}</div>
                  <div>
                    <span className="font-medium text-gray-500">Concepto:</span>
                  </div>
                  <div>{actionMilestone.label || actionMilestone.description || "—"}</div>
                  <div>
                    <span className="font-medium text-gray-500">Tipo:</span>
                  </div>
                  <div>
                    {actionMilestone.type === "fixed_amount"
                      ? "Importe fijo"
                      : actionMilestone.type === "percentage"
                        ? "Porcentaje"
                        : "Certificación mensual"}
                  </div>
                  {actionMilestone.type === "percentage" && actionMilestone.percentage && (
                    <>
                      <div>
                        <span className="font-medium text-gray-500">Porcentaje:</span>
                      </div>
                      <div>{actionMilestone.percentage}%</div>
                    </>
                  )}
                  {actionMilestone.notes && (
                    <>
                      <div>
                        <span className="font-medium text-gray-500">Notas:</span>
                      </div>
                      <div className="whitespace-pre-wrap">{actionMilestone.notes}</div>
                    </>
                  )}

                  <div className="col-span-2 border-b pb-1 mb-1 mt-1">
                    <span className="font-semibold">Importe y pago</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Planificado:</span>
                  </div>
                  <div className="font-semibold">{getMilestoneAmountLabel(actionMilestone)}</div>
                  {actionMilestone.type === "percentage" && actionMilestone.percentage && (
                    <>
                      <div>
                        <span className="font-medium text-gray-500">Base de cálculo:</span>
                      </div>
                      <div>{getMilestoneBaseLabel(actionMilestone)}</div>
                    </>
                  )}
                  <div>
                    <span className="font-medium text-gray-500">Forma de pago:</span>
                  </div>
                  <div>
                    {getPaymentMethodLabel(
                      actionMilestone.effectivePaymentMethod ?? actionMilestone.paymentMethod,
                    )}
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Plazo:</span>
                  </div>
                  <div>
                    {getPaymentTermLabel(actionMilestone.paymentTermBasis)}
                    {actionMilestone.paymentTerms
                      ? ` · ${actionMilestone.paymentTerms} días`
                      : ""}
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Fecha prevista:</span>
                  </div>
                  <div>{actionMilestone.dueDate ? new Date(actionMilestone.dueDate).toLocaleDateString() : "—"}</div>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeStatusDialog}>
                  Cancelar
                </Button>
                <Button
                  onClick={async () => {
                    const currentAction = pendingStatusAction;
                    closeStatusDialog();
                    await runStatusMutation(
                      currentAction.action,
                      currentAction.milestone
                    );
                  }}
                >
                  Confirmar y notificar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

      {/* Revert Status Dialog */}
      {actionMilestone && statusDialog === "revert_status" && (
        <Dialog
          open={true}
          onOpenChange={open => {
            if (!open) {
              closeStatusDialog();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Retroceder estado</DialogTitle>
              <DialogDescription>
                Este cambio quedará registrado en auditoría.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Hito:</span>{" "}
                  {actionMilestone.label || actionMilestone.description || "—"}
                </div>
                <div>
                  <span className="font-medium">Estado actual:</span>{" "}
                  {getMilestoneStatusLabel(actionMilestone.status, actionMilestone)}
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="revertTargetStatus">Estado destino</Label>
                <Select
                  value={revertTargetStatus}
                  onValueChange={setRevertTargetStatus}
                >
                  <SelectTrigger id="revertTargetStatus">
                    <SelectValue placeholder="Selecciona estado destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAllowedRevertTargets(actionMilestone).map(status => (
                      <SelectItem key={status} value={status}>
                        {getMilestoneStatusLabel(status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="revertReason">Motivo *</Label>
                <Textarea
                  id="revertReason"
                  value={revertReason}
                  onChange={e => setRevertReason(e.target.value)}
                  placeholder="Indica un motivo para retroceder el estado"
                />
                <p className="text-xs text-muted-foreground">
                  Entre 5 y 500 caracteres.
                </p>
              </div>

              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>Este cambio quedará registrado en auditoría.</span>
                </div>
              </div>

              {hasInvoiceData(actionMilestone) && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Este hito tiene datos de factura. No se borrarán, solo se
                  retrocederá el estado.
                </div>
              )}

              {hasAccountingNotice(actionMilestone) && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Ya existe aviso a contabilidad registrado. El retroceso no
                  borra ese historial.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeStatusDialog}>
                Cancelar
              </Button>
              <Button
                onClick={handleRevertStatus}
                disabled={
                  revertReason.trim().length < 5 ||
                  revertReason.trim().length > 500 ||
                  !revertTargetStatus ||
                  loadingAction === `revert_status:${actionMilestone.id}`
                }
              >
                {loadingAction === `revert_status:${actionMilestone.id}`
                  ? "Retrocediendo..."
                  : "Confirmar retroceso"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Repair Legacy Base Dialog */}
      {actionMilestone && statusDialog === "repair_legacy_base" && (
        <Dialog
          open={true}
          onOpenChange={open => {
            if (!open) closeStatusDialog();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revisar base histórica</DialogTitle>
              <DialogDescription>
                Confirma la base utilizada originalmente para este hito.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 text-sm">
              <div className="space-y-1">
                <div><span className="font-medium">Hito:</span> {actionMilestone.label || actionMilestone.description || "-"}</div>
                <div><span className="font-medium">Porcentaje:</span> {actionMilestone.percentage ?? "-"}%</div>
                <div><span className="font-medium">Importe persistido:</span> {formatMoney(getPersistedMilestoneAmount(actionMilestone))}</div>
                <div><span className="font-medium">Base sugerida:</span> {getSuggestedHistoricalBase(actionMilestone) !== null ? formatMoney(getSuggestedHistoricalBase(actionMilestone)) : "-"}</div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="legacyBaseValue">Base histórica sugerida</Label>
                <Input
                  id="legacyBaseValue"
                  value={getLegacyBaseValue()}
                  readOnly
                />
              </div>

              <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
                <Label htmlFor="legacyBaseConfirmed" className="text-sm font-medium">
                  Confirmo que esta base histórica es correcta
                </Label>
                <Switch
                  id="legacyBaseConfirmed"
                  checked={legacyBaseConfirmed}
                  onCheckedChange={setLegacyBaseConfirmed}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeStatusDialog}>
                Cancelar
              </Button>
              <Button
                onClick={handleRepairLegacyBase}
                disabled={
                  !legacyBaseConfirmed ||
                  !getLegacyBaseValue() ||
                  loadingAction === `repair_legacy_base:${actionMilestone.id}`
                }
              >
                {loadingAction === `repair_legacy_base:${actionMilestone.id}`
                  ? "Guardando..."
                  : "Confirmar base"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Cancel Dialog */}
      {actionMilestone && statusDialog === "cancel" && (
        <Dialog
          open={true}
          onOpenChange={open => {
            if (!open) {
              closeStatusDialog();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancelar hito de facturación</DialogTitle>
              <DialogDescription>
                Vas a cambiar este hito a Cancelado. ¿Estás seguro?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {renderMonthlyCertificationWarning(actionMilestone)}
              <div className="mb-4 space-y-2 text-sm">
                <div>
                  <span className="font-medium">Instalación:</span>{" "}
                  {installation.clientName ||
                    installation.clientId ||
                    installation.id}
                </div>
                <div>
                  <span className="font-medium">Hito:</span>{" "}
                  {actionMilestone.label || actionMilestone.description || "—"}
                </div>
                <div>
                  <span className="font-medium">Planificado:</span>{" "}
                  {getMilestoneAmountLabel(actionMilestone)}
                </div>
                <div>
                  <span className="font-medium">Estado actual:</span>{" "}
                  {getMilestoneStatusLabel(actionMilestone.status, actionMilestone)}
                </div>
                <div>
                  <span className="font-medium">Nuevo estado:</span> Cancelado
                </div>
              </div>
              <Label htmlFor="cancelReason">Motivo de cancelación *</Label>
              <Textarea
                id="cancelReason"
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="Especifica el motivo de la cancelación"
                className="mt-2"
              />
              {cancelReason.trim() === "" && loadingAction === "cancel" && (
                <p className="text-sm text-red-500 mt-1">
                  El motivo de cancelación es obligatorio
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  closeStatusDialog();
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCancel}
                disabled={
                  cancelReason.trim().length < 5 ||
                  cancelReason.trim().length > 500 ||
                  loadingAction === `cancel:${actionMilestone.id}`
                }
                className={
                  loadingAction === `cancel:${actionMilestone.id}`
                    ? "opacity-50"
                    : ""
                }
              >
                {loadingAction === `cancel:${actionMilestone.id}`
                  ? "Cancelando..."
                  : "Confirmar cancelación"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Mark Invoiced Dialog */}
      {actionMilestone && statusDialog === "mark_invoiced" && (
        <Dialog
          open={true}
          onOpenChange={open => {
            if (!open) {
              closeStatusDialog();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{actionMilestone.invoiceNumber && actionMilestone.invoiceFiscalVerified !== true ? "Completar desglose fiscal" : "Marcar hito como facturado"}</DialogTitle>
              <DialogDescription>
                {actionMilestone.invoiceNumber && actionMilestone.invoiceFiscalVerified !== true
                  ? "Completa el desglose de la factura existente. No se cambiarán número, fecha ni total; si el total no coincide, requiere revisión autorizada."
                  : `Introduce los datos de la factura para el hito “${actionMilestone.label || ""}”.`}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {renderMonthlyCertificationWarning(actionMilestone)}
              <div className="grid gap-2">
                <Label htmlFor="invoiceNumber">Número de factura *</Label>
                <Input
                  id="invoiceNumber"
                  value={invoiceNumber}
                  onChange={e => setInvoiceNumber(e.target.value)}
                  placeholder="Ej: FA2024-001"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="invoiceDate">Fecha de factura *</Label>
                <Input
                  id="invoiceDate"
                  type="date"
                  value={invoiceDate}
                  onChange={e => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="invoiceTaxableBase">Base imponible (€) *</Label>
                <Input id="invoiceTaxableBase" type="number" min="0" step="0.01" value={invoiceTaxableBase} onChange={e => handleInvoiceBaseChange(e.target.value)} />
              </div>
              <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                <div className="grid min-w-0 gap-2">
                  <Label htmlFor="invoiceVatRate">Tipo de IVA</Label>
                  <BillingVatRateSelect id="invoiceVatRate" value={invoiceVatRate} onValueChange={handleInvoiceVatRateChange} />
                  <Label htmlFor="invoiceTaxAmount">Cuota IVA (€) *</Label>
                  <Input id="invoiceTaxAmount" type="number" min="0" step="0.01" value={invoiceTaxAmount} onChange={e => setInvoiceTaxAmount(e.target.value)} />
                </div>
                <div className="grid min-w-0 gap-2">
                  <Label htmlFor="invoiceWithholdingAmount">Retención (€) *</Label>
                  <Input id="invoiceWithholdingAmount" type="number" min="0" step="0.01" value={invoiceWithholdingAmount} onChange={e => setInvoiceWithholdingAmount(e.target.value)} />
                </div>
              </div>
              <p className="text-sm font-medium">Total exigible: {(() => {
                const base = parseMoney(invoiceTaxableBase);
                const tax = parseMoney(invoiceTaxAmount);
                const withholding = parseMoney(invoiceWithholdingAmount);
                return base === null || tax === null || withholding === null || base + tax - withholding <= 0
                  ? "—"
                  : formatMoney(base + tax - withholding);
              })()}</p>
              <div className="grid gap-2">
                <Label htmlFor="invoiceFile">
                  Factura adjunta (PDF o imagen)
                </Label>
                <Input
                  id="invoiceFile"
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={e => setInvoiceFile(e.target.files?.[0] || null)}
                />
                {invoiceFile && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Upload className="h-3.5 w-3.5" />
                    {invoiceFile.name}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  closeStatusDialog();
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleMarkInvoiced}
                disabled={
                  !invoiceNumber ||
                  !invoiceDate ||
                  loadingAction === `mark_invoiced:${actionMilestone.id}`
                }
                className={
                  loadingAction === `mark_invoiced:${actionMilestone.id}`
                    ? "opacity-50"
                    : ""
                }
              >
                {loadingAction === `mark_invoiced:${actionMilestone.id}`
                  ? "Guardando..."
                  : "Marcar facturado"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
